import React from "react";

import cloneDeep from "lodash/cloneDeep";

import Modal from "@cloudscape-design/components/modal";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Input from "@cloudscape-design/components/input";
import Tabs from "@cloudscape-design/components/tabs";
import Button from "@cloudscape-design/components/button";
import Grid from "@cloudscape-design/components/grid";
import Box from "@cloudscape-design/components/box";
import Textarea from "@cloudscape-design/components/textarea";
import Badge from "@cloudscape-design/components/badge";
import Link from "@cloudscape-design/components/link";
import Spinner from "@cloudscape-design/components/spinner";
import Container from "@cloudscape-design/components/container";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Icon from "@cloudscape-design/components/icon";
import Header from "@cloudscape-design/components/header";

import Avatar from "react-avatar";

/* Recoil */
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { userStore } from "../stores/access";
import { configStore } from "../stores/config";
import { gameStore } from "../stores/games";
import { paymentStore } from "../stores/payments";
import {
	createBalanceCreditLimitRequestToken,
	createBalanceCreditLimitState,
	getBalanceCreditLimitContext,
	isSameBalanceCreditLimitContext,
	normalizeBalanceCreditLimitState,
	playerModalStore,
} from "../stores/playerModal";
import { playerPageStore } from "../stores/playerPage";
import { getRecoil } from "recoil-nexus";
import { fortuneWheelModalStore } from "../stores/fortuneWheelModal";
import Select from "@cloudscape-design/components/select";
import { multiWithdrawalStore } from "../stores/multiWithdrawal";
import { gameDetailLogModalStore } from "../stores/gameDetailLogModal";
import PlayerProfilePartnerSettingsSection from "./PlayerProfilePartnerSettingsSection";
import PlayerProfilePostbacksSection from "./PlayerProfilePostbacksSection";
import PlayerProfileDepositLadderSection from "./PlayerProfileDepositLadderSection";
import PlayerCallcenterTriggerModal from "./PlayerCallcenterTriggerModal";
import PlayerProfilePartnerMetadataSection from "./PlayerProfilePartnerMetadataSection";
import PlayerProfilePartnerStatsSection from "./PlayerProfilePartnerStatsSection";
import { userActionLogStore } from "../stores/userActionLog";
/* ------- */

const typeMap = {
	1: "deposit",
	2: "withdraw",
	3: "refund",
	7: "promo code",
	8: "wheel",
	9: "VK bonus",
	10: "aff",
	12: "boost",
	13: "system",
	19: "TG bonus",
	24: "Rain",
	25: "loyalty",
	35: "rakeback",
	45: "reload",
	55: "lvlup",
	69: "race",
	88: "weekly giveaway",
	89: "monthly giveaway",
	99: "bet",
	115: "fortuneWheel",
	170: "sweepstakes",
	180: "achievement",
	181: "deposit ladder",
	190: "Phone bonus",
	191: "Email bonus",
};

const colorMap = {
	1: "green",
	2: "red",
	3: "teal",
	7: "cyan",
	8: "cyan",
	9: "vk",
	10: "purple",
	12: "orange",
	13: "dark",
	19: "cyan",
	24: "cyan",
	25: "lightOrange",
	35: "darkYellow",
	45: "darkYellow",
	55: "darkYellow",
	69: "orange",
	88: "darkYellow",
	89: "darkYellow",
	99: "blue",
	115: "magentaSecondary",
	170: "gold",
	180: "gold",
	181: "gold",
	190: "cyan",
	191: "cyan",
};

const readRewardConditions = (value) => {
	if (!value) return {};
	if (typeof value === "object" && !Array.isArray(value)) return value;

	try {
		const parsed = JSON.parse(value);
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
	} catch (error) {
		return {};
	}
};

const formatRewardNumber = (value) => {
	const number = Number(value);
	if (!Number.isFinite(number)) return "N/A";

	return new Intl.NumberFormat("ru-RU", {
		maximumFractionDigits: 2,
	}).format(number);
};

const getRowRewards = (item) => (Array.isArray(item?.milestoneRewards) ? item.milestoneRewards : [item]);

const addMilestoneDay = (reward, value) => {
	const day = Number(reward?.milestoneDay);
	return Number.isFinite(day) && day > 0 ? `Day ${day}: ${value}` : value;
};

const getRewardTypeText = (reward) => {
	if (reward?.sourceRewardType === "chest") {
		return reward.chestName || "Chest";
	}
	if (reward?.rewardType === "freespins") {
		return "FS";
	}

	return reward?.sourceRewardType || reward?.rewardType || "N/A";
};

const getRewardValueText = (reward) => {
	console.log("reward: ", reward);
	if (reward?.sourceRewardType === "chest" && (!reward.claimed || reward.rewardType === "chest")) {
		return "N/A";
	}

	if (reward?.rewardType === "freespins") {
		const spins = formatRewardNumber(reward.rewardValue);
		const conditions = readRewardConditions(reward.rewardConditions);
		const betAmount = Number(conditions.betAmount);
		const total = Number(reward.rewardValue) * betAmount;

		return Number.isFinite(total) ? `${spins} FS (${formatRewardNumber(total)} RUB)` : `${spins} FS`;
	}

	if (reward?.rewardType === "money") {
		const value = Number(reward.rewardValue);
		return Number.isFinite(value) ? `${formatRewardNumber(value / 100)} RUB` : "N/A";
	}

	if (reward?.rewardType === "tickets") {
		const value = formatRewardNumber(reward.rewardValue);
		return value === "N/A" ? value : `${value} tickets`;
	}

	if (reward?.rewardValue === null || typeof reward?.rewardValue === "undefined" || reward.rewardValue === "") {
		return "N/A";
	}

	return formatRewardNumber(reward.rewardValue);
};

const renderRewardLines = (lines) =>
	lines.length === 0 ? (
		"N/A"
	) : lines.length === 1 ? (
		lines[0]
	) : (
		<div>
			{lines.map((line, index) => (
				<div key={`${index}-${line}`}>{line}</div>
			))}
		</div>
	);

const renderRewardType = (item) => renderRewardLines(getRowRewards(item).map((reward) => addMilestoneDay(reward, getRewardTypeText(reward))));

const renderRewardValue = (item) => renderRewardLines(getRowRewards(item).map((reward) => addMilestoneDay(reward, getRewardValueText(reward))));

const renderRewardConditions = (item) => {
	const conditions = item?.rewardConditions;
	if (!conditions) return "N/A";

	if (typeof conditions === "object") {
		return Object.keys(conditions).length > 0 ? JSON.stringify(conditions) : "N/A";
	}

	return String(conditions);
};

class PlayerProfileModal extends React.PureComponent {
	constructor(props) {
		super(props);
		this.commentTimeouts = { private: null, public: null };
		this.filterTimeout = null;
		this.gameLogFilterTimeout = null;
		this.profileLoadVersion = 0;
		this.unmounted = false;
		this.pendingTimeouts = new Set();
		this.scrollRef = React.createRef();
		this.observer = null;

		this.state = {
			filteredData: [],
			isLoading: false,
			privateComment: "",
			publicComment: "",
			callcenterTriggerModalVisible: false,
			activeRuleTooltip: null,
			resetId: 0,
			casinoBalance: {
				loading: true,
				total: 0,
			},
			wheel: {
				total: 0,
				loading: true,
			},
			promocode: {
				total: 0,
				loading: true,
			},
			aff: {
				total: 0,
				loading: true,
			},
			boost: {
				total: 0,
				loading: true,
			},
			vkBonus: {
				total: 0,
				loading: true,
			},
			rakeback: {
				loading: true,
				total: 0,
			},
			purchasedTickets: {
				loading: true,
				total: 0,
			},
			totalReferer: {
				loading: true,
				total: 0,
			},
			welcomeBonus: {
				data: null,
				uid: null,
				project: null,
			},
			rules: {
				loading: true,
				data: [],
			},
			externalRules: {
				loading: false,
				data: [],
			},
			paymentsStats: {
				statsDeposit: {
					loading: true,
					deposits: { count: 0, total: 0 },
					depositsMonth: { count: 0, total: 0 },
					depositsWeek: { count: 0, total: 0 },
					depositsDay: { count: 0, total: 0 },
					firstDeposit: undefined,
				},
				statsWithdrawals: {
					loading: true,
					withdrawals: { count: 0, total: 0 },
					withdrawalsMonth: { count: 0, total: 0 },
					withdrawalsWeek: { count: 0, total: 0 },
					withdrawalsDay: { count: 0, total: 0 },
				},
			},
			userStats: {
				loading: true,
				data: [],
			},
			userStatsSearch: {
				data: [],
			},
			logCasino: {
				loading: true,
				data: [],
			},
		};
	}

	scheduleTimeout = (callback, delay = 0) => {
		const timeout = setTimeout(() => {
			this.pendingTimeouts.delete(timeout);
			if (!this.unmounted) callback();
		}, delay);

		this.pendingTimeouts.add(timeout);
		return timeout;
	};

	cancelTimeout = (timeout) => {
		if (!timeout) return;
		clearTimeout(timeout);
		this.pendingTimeouts.delete(timeout);
	};

	clearPendingTimeouts = () => {
		this.pendingTimeouts.forEach((timeout) => clearTimeout(timeout));
		this.pendingTimeouts.clear();
		this.commentTimeouts = { private: null, public: null };
		this.filterTimeout = null;
		this.gameLogFilterTimeout = null;
	};

	getProjectPageSize(config, project, storageKey) {
		const projectKey = String(Number(project) || 0);
		let storedValues = config.perPageByProject;

		try {
			const localValues = JSON.parse(localStorage.getItem(storageKey));
			if (localValues && typeof localValues === "object" && !Array.isArray(localValues)) storedValues = { ...localValues, ...storedValues };
		} catch (error) {
			storedValues = config.perPageByProject;
		}

		const storedValue = Number(storedValues?.[projectKey]);
		const allowedValues = config.preferences.perPage.map((option) => option.value);
		return allowedValues.includes(storedValue) ? storedValue : config.defaultPerPage;
	}

	saveProjectPageSize(config, project, pageSize, storageKey) {
		const projectKey = String(Number(project) || 0);
		let storedValues = {};

		try {
			const localValues = JSON.parse(localStorage.getItem(storageKey));
			if (localValues && typeof localValues === "object" && !Array.isArray(localValues)) storedValues = localValues;
		} catch (error) {
			storedValues = {};
		}

		const perPageByProject = {
			...storedValues,
			...config.perPageByProject,
			[projectKey]: Number(pageSize),
		};

		localStorage.setItem(storageKey, JSON.stringify(perPageByProject));
		return perPageByProject;
	}

	showRuleTooltip = (tooltipKey) => {
		this.setState({ activeRuleTooltip: tooltipKey });
	};

	hideRuleTooltip = () => {
		this.setState({ activeRuleTooltip: null });
	};

	getVerifyStatusLabel = () => {
		const verify = this.props.playerPage.data.verify || {};

		if (verify?.status === 2) return "yes";
		if (verify?.status === 3) return "rejected";
		if (verify?.status === 1) return "waiting";

		return "no";
	};

	getSelfExclusionLabel = () => {
		const selfExclude = this.props.playerPage.data.selfExclude || {};

		if (!selfExclude.isExcluded) return "no";

		return "yes (till " + new Date(selfExclude.till).toLocaleDateString() + " " + new Date(selfExclude.till).toLocaleTimeString() + ")";
	};

	renderRuleTag = (el, keyPrefix, index, totalCount) => {
		const tooltipKey = `${keyPrefix}-${el.tag || "unknown"}-${index}`;
		const hasDescription = Boolean(el.description);
		const isTooltipVisible = hasDescription && this.state.activeRuleTooltip === tooltipKey;
		const tooltipPositionStyle = index === 0 ? { left: 0, transform: "none" } : index === totalCount - 1 ? { left: "auto", right: 0, transform: "none" } : { left: "50%", transform: "translateX(-50%)" };

		return (
			<div
				key={tooltipKey}
				style={{
					position: "relative",
					display: "inline-block",
					marginRight: 5,
					marginBottom: 2,
				}}
				onMouseEnter={() => this.showRuleTooltip(tooltipKey)}
				onMouseLeave={this.hideRuleTooltip}>
				<div
					style={{
						backgroundColor: el.color || "",
						border: "1px solid #ccc",
						color: "white",
						padding: 5,
						borderRadius: 10,
						outline: "none",
						borderColor: "transparent",
						cursor: hasDescription ? "help" : "default",
					}}>
					{el.tag}
				</div>
				{isTooltipVisible && (
					<div
						style={{
							position: "absolute",
							bottom: "calc(100% + 8px)",
							minWidth: 180,
							maxWidth: "min(320px, calc(100vw - 48px))",
							zIndex: 1000,
							...tooltipPositionStyle,
						}}>
						<Container variant="stacked">
							<Box variant="p" color="inherit" fontSize="body-m">
								<div
									style={{
										whiteSpace: "pre-wrap",
										wordBreak: "break-word",
										lineHeight: "20px",
										textAlign: "left",
										margin: 0,
									}}>
									{el.description}
								</div>
							</Box>
						</Container>
					</div>
				)}
			</div>
		);
	};

	componentDidMount() {
		const hasCreditAccess = Number(this.props.user.level) >= 6 || this.props.user.showColumn.includes("balanceCredit");
		if (hasCreditAccess && this.props.playerPage.show && this.props.playerPage?.data?.id) {
			this.loadBalanceCreditLimit();
		}
	}

	componentDidUpdate(prevProps) {
		const { Interface, playerPage, user } = this.props;
		const currentCreditContext = getBalanceCreditLimitContext(playerPage, user);
		const previousCreditContext = getBalanceCreditLimitContext(prevProps.playerPage, prevProps.user);
		const hasCreditAccess = Number(user.level) >= 6 || user.showColumn.includes("balanceCredit");

		const profileDataId = playerPage?.data?.id;
		const previousProfileDataId = prevProps.playerPage?.data?.id;
		const profileOpened = !prevProps.playerPage.show && playerPage.show;

		const profileIdentityChanged =
			Boolean(profileDataId) &&
			(profileDataId !== previousProfileDataId || playerPage.uid !== prevProps.playerPage.uid);

		const currentProject = playerPage?.data?.project;
		const previousProject = prevProps.playerPage?.data?.project;

		const confirmedProjectChanged =
			typeof currentProject !== "undefined" &&
			currentProject !== null &&
			(typeof previousProject === "undefined" || 
				previousProject === null || 
				String(currentProject) !== String(previousProject));
		
		const creditContextChanged =
			confirmedProjectChanged ||
			!isSameBalanceCreditLimitContext(currentCreditContext, previousCreditContext);
		
		const profileChanged =
			playerPage?.data?.id &&
			(playerPage.data.id !== prevProps.playerPage?.data?.id ||
				playerPage.uid !== prevProps.playerPage?.uid ||
				String(playerPage.data.project || 0) !== 
					String(prevProps.playerPage?.data?.project || 0));
		
		if (
			hasCreditAccess && 
			playerPage.show && 
			profileDataId && 
			(profileOpened || profileIdentityChanged || creditContextChanged)
		) {
			this.loadBalanceCreditLimit();
		} else if (
			!hasCreditAccess && 
			(Number(prevProps.user.level) >= 6 || 
			prevProps.user.showColumn.includes("balanceCredit"))
		) {
			this.props.setPlayerModal((playerModal) => ({
				...playerModal,
				balanceCreditLimit: createBalanceCreditLimitState(currentCreditContext),
			}));
		}

		if (profileChanged) {
			const loadVersion = ++this.profileLoadVersion;
			const requestUid = playerPage.uid;
			const requestPlayerId = playerPage.data.id;
			const requestProject = playerPage.data.project;

			this.cancelTimeout(this.commentTimeouts.private);
			this.cancelTimeout(this.commentTimeouts.public);

			this.props.resetUserActionLog();
			this.setState({
				isLoading: true,
				filteredData: [],
				privateComment: playerPage.data.comment || "",
				publicComment: playerPage.data.publicComment || "",
				paymentsStats: {
					...this.state.paymentsStats,
					statsDeposit: { ...this.state.paymentsStats.statsDeposit, loading: true },
					statsWithdrawals: { ...this.state.paymentsStats.statsWithdrawals, loading: true },
				},
				wheel: { ...this.state.wheel, loading: true },
				promocode: { ...this.state.promocode, loading: true },
				aff: { ...this.state.aff, loading: true },
				boost: { ...this.state.boost, loading: true },
				vkBonus: { ...this.state.vkBonus, loading: true },
				rakeback: { ...this.state.rakeback, loading: true },
				purchasedTickets: { ...this.state.purchasedTickets, loading: true },
				totalReferer: { ...this.state.totalReferer, loading: true },
				welcomeBonus: {
					data: null,
					uid: requestUid,
					project: requestProject,
				},
				casinoBalance: { ...this.state.casinoBalance, loading: true },
				userStats: { ...this.state.userStats, loading: true },
				logCasino: { ...this.state.logCasino, loading: true },
				rules: { ...this.state.rules, loading: true },
				externalRules: { ...this.state.externalRules, loading: true },
			});

			const requests = [];
			const results = new Map();
			const isCurrentLoad = () => {
				return !this.unmounted && loadVersion === this.profileLoadVersion && this.props.playerPage?.show && this.props.playerPage.uid === requestUid && this.props.playerPage.data?.id === requestPlayerId && String(this.props.playerPage.data?.project || 0) === String(requestProject || 0);
			};

			let flushTimeout = null;
			const flushResults = () => {
				flushTimeout = null;
				if (!isCurrentLoad() || results.size === 0) return;

				const batch = new Map(results);
				results.clear();

				if (batch.has("logCasino")) {
					const logCasino = batch.get("logCasino");
					this.props.setPlayerPage((currentPlayerPage) => {
						if (currentPlayerPage.uid !== requestUid || currentPlayerPage.data?.id !== requestPlayerId) return currentPlayerPage;
						return {
							...currentPlayerPage,
							gameLog: {
								...currentPlayerPage.gameLog,
								pagination: {
									current: 1,
									total: Math.ceil(logCasino.length / getRecoil(configStore).players.gameLogShort.perPage),
								},
							},
						};
					});
				}

				this.setState((prevState) => ({
					isLoading: false,
					paymentsStats: {
						...prevState.paymentsStats,
						statsDeposit: batch.has("deposits") ? { ...prevState.paymentsStats.statsDeposit, ...batch.get("deposits"), loading: false } : prevState.paymentsStats.statsDeposit,
						statsWithdrawals: batch.has("withdrawals") ? { ...prevState.paymentsStats.statsWithdrawals, ...batch.get("withdrawals"), loading: false } : prevState.paymentsStats.statsWithdrawals,
					},
					wheel: batch.has("wheel") ? { total: batch.get("wheel"), loading: false } : prevState.wheel,
					promocode: batch.has("promocode") ? { total: batch.get("promocode"), loading: false } : prevState.promocode,
					aff: batch.has("aff") ? { total: batch.get("aff"), loading: false } : prevState.aff,
					boost: batch.has("boost") ? { total: batch.get("boost"), loading: false } : prevState.boost,
					vkBonus: batch.has("vkBonus") ? { total: batch.get("vkBonus"), loading: false } : prevState.vkBonus,
					rakeback: batch.has("rakeback") ? { total: batch.get("rakeback"), loading: false } : prevState.rakeback,
					purchasedTickets: batch.has("purchasedTickets") ? { total: batch.get("purchasedTickets"), loading: false } : prevState.purchasedTickets,
					totalReferer: batch.has("totalReferer") ? { total: batch.get("totalReferer"), loading: false } : prevState.totalReferer,
					welcomeBonus: batch.has("welcomeBonus")
						? {
								data: batch.get("welcomeBonus"),
								uid: requestUid,
								project: requestProject,
						  }
						: prevState.welcomeBonus,
					casinoBalance: batch.has("casinoBalance") ? { total: batch.get("casinoBalance"), loading: false } : prevState.casinoBalance,
					userStats: batch.has("userStats") ? { data: batch.get("userStats"), loading: false } : prevState.userStats,
					logCasino: batch.has("logCasino") ? { data: batch.get("logCasino"), loading: false } : prevState.logCasino,
					rules: batch.has("rules") ? { data: batch.get("rules"), loading: false } : prevState.rules,
					externalRules: batch.has("externalRules") ? { data: batch.get("externalRules"), loading: false } : prevState.externalRules,
				}));
			};

			const scheduleResultsFlush = () => {
				if (flushTimeout) return;
				flushTimeout = this.scheduleTimeout(flushResults, 50);
			};

			const addRequest = (type, promise, transform = (value) => value, fallback = 0) => {
				requests.push(
					promise
						.then((data) => {
							results.set(type, transform(data));
							scheduleResultsFlush();
						})
						.catch((error) => {
							if (error?.name !== "AbortError") console.error(`Failed to load ${type}:`, error);
							results.set(type, fallback);
							scheduleResultsFlush();
						}),
				);
			};

			if (user.showColumn.includes("antiFrod") || user.level >= 6) {
				const pageSize = this.getProjectPageSize(getRecoil(configStore).players.multiWithdrawals, playerPage.data.project, "players.multiWithdrawals.perPageByProject");
				Interface.loadMultiWithdrawalInfo(playerPage.uid, "withdrawals", playerPage.data.project, { page: 1, limit: pageSize });
			}

			addRequest(
				"deposits",
				Interface.loadPeriodStatsDeposit(requestUid, requestProject),
				(deposits) => ({
					deposits: { count: deposits.deposits_count, total: deposits.deposits_total },
					depositsMonth: { count: deposits.deposits_month_count, total: deposits.deposits_month_total },
					depositsWeek: { count: deposits.deposits_week_count, total: deposits.deposits_week_total },
					depositsDay: { count: deposits.deposits_day_count, total: deposits.deposits_day_total },
					firstDeposit: deposits.deposits_count > 0 ? deposits.first_deposit_date * 1000 : undefined,
				}),
				{
					deposits: { count: 0, total: 0 },
					depositsMonth: { count: 0, total: 0 },
					depositsWeek: { count: 0, total: 0 },
					depositsDay: { count: 0, total: 0 },
					firstDeposit: undefined,
				},
			);

			addRequest(
				"withdrawals",
				Interface.loadPeriodStatsWithdrawal(requestUid, requestProject),
				(withdrawals) => ({
					withdrawals: { count: withdrawals.withdraws_count, total: withdrawals.withdraws_total },
					withdrawalsMonth: { count: withdrawals.withdraws_month_count, total: withdrawals.withdraws_month_total },
					withdrawalsWeek: { count: withdrawals.withdraws_week_count, total: withdrawals.withdraws_week_total },
					withdrawalsDay: { count: withdrawals.withdraws_day_count, total: withdrawals.withdraws_day_total },
				}),
				{
					withdrawals: { count: 0, total: 0 },
					withdrawalsMonth: { count: 0, total: 0 },
					withdrawalsWeek: { count: 0, total: 0 },
					withdrawalsDay: { count: 0, total: 0 },
				},
			);

			addRequest("vkBonus", Interface.loadTotalVkBonus(requestUid, requestProject));
			addRequest("totalReferer", Interface.loadTotalReferer(requestUid, requestProject), (data) => data?.total || 0);
			addRequest("wheel", Interface.loadTotalWheel(requestUid, requestProject));
			addRequest("promocode", Interface.loadTotalPromocode(requestUid, requestProject));
			addRequest("aff", Interface.loadTotalAff(requestUid, requestProject));
			addRequest("boost", Interface.loadTotalBoost(requestUid, requestProject));
			addRequest("rakeback", Interface.loadTotalRakeback(requestUid, requestProject));
			addRequest("welcomeBonus", Interface.loadWelcomeBonusDetails(requestUid, requestProject), (data) => data || null, null);

			if (user.showColumn.includes("purchasedTickets") || user.level >= 6) {
				addRequest("purchasedTickets", Interface.loadTotalPurchasedTickets(requestUid, requestProject), (data) => data?.totalPurchased || 0);
			} else {
				results.set("purchasedTickets", 0);
			}

			if (user.showColumn.includes("showPlayerRules") || user.level >= 6) {
				addRequest("rules", Interface.loadCheckRules(requestUid, requestProject), (data) => data || [], []);
				addRequest("externalRules", Interface.loadExternalCheckRules(requestUid, requestProject), (data) => data || [], []);
			} else {
				results.set("rules", []);
				results.set("externalRules", []);
			}

			if (playerPage.data.casinoID) {
				addRequest("casinoBalance", Interface.loadTotalCasinoBalance(playerPage.data.casinoID, requestProject));
				addRequest("userStats", Interface.loadPlayerStats(playerPage.data.casinoID, requestProject), (data) => data || [], []);

				if (user.showColumn.includes("gamesSessions") || user.level >= 6) {
					addRequest(
						"logCasino",
						Interface.loadPlayerSession(playerPage.data.casinoID, user.brand.casinoID, requestProject),
						(data) =>
							(data || []).map((item) => ({
								...item,
								filtered: true,
								gameSearch: Interface.getGameByID(item.gameID),
							})),
						[],
					);
				} else {
					results.set("logCasino", []);
				}
			} else {
				results.set("casinoBalance", 0);
				results.set("userStats", []);
				results.set("logCasino", []);
			}

			scheduleResultsFlush();
			this.setupIntersectionObserver();

			Promise.all(requests)
				.then(() => {
					if (isCurrentLoad()) flushResults();
				})
				.catch((err) => {
					if (!isCurrentLoad()) return;
					console.error("Unexpected error during data loading:", err);
					this.setState({ isLoading: false });
				});
		}
	}

	loadBalanceCreditLimit() {
		const context = getBalanceCreditLimitContext(this.props.playerPage, this.props.user);
		if (context.project === null || context.brand === null || !context.login) return;

		const requestToken = createBalanceCreditLimitRequestToken();
		this.props.setPlayerModal((playerModal) => ({
			...playerModal,
			balanceCreditLimit: {
				...createBalanceCreditLimitState({ ...context, requestToken }),
				loading: true,
			},
		}));

		this.props.Interface.request("get", { class: "users", module: "wallet", method: "balanceCreditLimit" })
			.then((response) => {
				const limitState = normalizeBalanceCreditLimitState(response);
				const currentContext = getBalanceCreditLimitContext(this.props.playerPage, this.props.user);
				if (!isSameBalanceCreditLimitContext(currentContext, context)) return;

				this.props.setPlayerModal((playerModal) => {
					if (playerModal.balanceCreditLimit?.requestToken !== requestToken) return playerModal;

					return {
						...playerModal,
						balanceCreditLimit: {
							...limitState,
							...context,
							loading: false,
							loaded: true,
							error: null,
							requestToken,
						},
					};
				});
			})
			.catch((error) => {
				const currentContext = getBalanceCreditLimitContext(this.props.playerPage, this.props.user);
				if (!isSameBalanceCreditLimitContext(currentContext, context)) return;

				const errorText = (error && (error.text || error.message)) || "Failed to load balance credit limit";
				this.props.setPlayerModal((playerModal) => {
					if (playerModal.balanceCreditLimit?.requestToken !== requestToken) return playerModal;

					return {
						...playerModal,
						balanceCreditLimit: {
							...createBalanceCreditLimitState({ ...context, requestToken }),
							error: errorText,
						},
					};
				});

				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	handleKeyPress = (event, type) => {
		if (event.detail.key === "Enter") {
			if (type === "winner") {
				this.scheduleTimeout(() => {
					this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid);
				}, 0);
			}
			if (type === "promocode") {
				this.scheduleTimeout(() => {
					this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project);
				}, 0);
			}
			if (type === "rain") {
				this.scheduleTimeout(() => {
					this.props.Interface.loadUserRain(this.props.playerPage.uid, this.props.playerPage.data.project);
				}, 0);
			}
			if (type === "allPromocode") {
				this.scheduleTimeout(() => {
					this.props.Interface.loadAllUserPromo(this.props.playerPage.uid, this.props.playerPage.data.project);
				}, 0);
			}
		}
	};

	handleInputDebounceChange = (input, type) => {
		switch (type) {
			case "winner": {
				this.props.setPlayerPage({
					...this.props.playerPage,
					bonuses: {
						...this.props.playerPage.bonuses,
						fortuneWheel: {
							...this.props.playerPage.bonuses.fortuneWheel,
							currentSearch: input.detail.value,

							pagination: {
								...this.props.playerPage.bonuses.fortuneWheel.pagination,
								current: 1,
							},
						},
					},
				});

				if (input.detail.value.length === 0) {
					this.scheduleTimeout(() => this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid), 250);
				}
				break;
			}

			case "allPromocode": {
				this.props.setPlayerPage({
					...this.props.playerPage,
					bonuses: {
						...this.props.playerPage.bonuses,
						allPromocode: {
							...this.props.playerPage.bonuses.allPromocode,
							currentSearch: input.detail.value,
							pagination: {
								...this.props.playerPage.bonuses.allPromocode.pagination,
								current: 1,
							},
						},
					},
				});

				if (input.detail.value.length === 0) {
					this.scheduleTimeout(() => this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project), 250);
				}
				break;
			}

			case "promocode": {
				this.props.setPlayerPage({
					...this.props.playerPage,
					bonuses: {
						...this.props.playerPage.bonuses,
						promocode: {
							...this.props.playerPage.bonuses.promocode,
							currentSearch: input.detail.value,
							pagination: {
								...this.props.playerPage.bonuses.promocode.pagination,
								current: 1,
							},
						},
					},
				});

				if (input.detail.value.length === 0) {
					this.scheduleTimeout(() => this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project), 250);
				}
				break;
			}

			case "rain": {
				this.props.setPlayerPage({
					...this.props.playerPage,
					bonuses: {
						...this.props.playerPage.bonuses,
						rain: {
							...this.props.playerPage.bonuses.rain,
							currentSearch: input.detail.value,
							pagination: {
								...this.props.playerPage.bonuses.rain.pagination,
								current: 1,
							},
						},
					},
				});

				if (input.detail.value.length === 0) {
					this.scheduleTimeout(() => this.props.Interface.loadUserRain(this.props.playerPage.uid, this.props.playerPage.data.project), 250);
				}
				break;
			}

			default: {
				break;
			}
		}
	};

	toggleValueModal(type) {
		switch (type) {
			case 0:
				this.props.setPlayerModal((playerModal) => ({ ...playerModal, show: 1, type, title: "Enter amount", description: "Amount", units: "RUB." }));
				break;
			case 1:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 1, type, title: "Enter amount", description: "Amount", units: "RUB." });
				break;
			case 2:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 2, type, title: "Enter amount", description: "Amount", units: "RUB." });
				break;
			case 3:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 1, type, title: "Enter amount", description: "Amount", units: "RUB." });
				break;
			case 4:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 1, type, title: "Enter reason", description: "Reason", units: "Refer to tos" });
				break;
			case 5:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 1, type, title: "Enter reason", description: "Reason", units: "Refer to tos" });
				break;
			case 6:
				this.props.setPlayerModal({
					...this.props.playerModal,
					show: 1,
					type,
					title: "Enter value",
					description: "Value",
					units: "RUB.",
					description2: false,
					units2: false,
					promoReasonSelected: null,
					promoGameSelected: null,
					promoReasonOptions: [],
					promoReasonsLoading: true,
					promoReasonsError: "",
					promoReasonSubmitting: false,
				});
				break;
			case 7:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 1, type, title: "Enter value", description: "Value", units: "RUB.", description2: "Count", units2: "Activations" });
				break;
			case 10:
				this.props.setPlayerModal({
					...this.props.playerModal,
					show: 1,
					type,
					title: "Create Freespins",
					providerSelected: null,
					gameSelected: null,
					availableGames: [],
					spinsCount: "",
					betAmount: "",
					bonusName: "",
					expiresAt: "",
				});
				break;
			case 11:
				this.props.setPlayerModal({
					...this.props.playerModal,
					show: 1,
					type,
					title: "VIP FS",
					providerSelected: null,
					gameSelected: null,
					availableGames: [],
					spinsCount: "",
					betAmount: "",
					bonusName: "",
					expiresAt: "",
					vipReasonSelected: null,
					vipReasonOptions: [],
					vipRulesLoading: false,
					vipRulesError: "",
					vipCheckLoading: false,
					vipCheckStatus: "idle",
					vipCheckLogId: null,
					vipCheckError: "",
					vipCheckResponse: null,
					vipGrantLoading: false,
				});
				break;
			default:
				this.props.setPlayerModal({ ...this.props.playerModal, show: 0, type });
				break;
		}
	}

	openCreateFreespinsModal() {
		this.toggleValueModal(10);
	}

	openCallcenterTriggerModal = () => {
		this.setState({ callcenterTriggerModalVisible: true });
	};

	closeCallcenterTriggerModal = () => {
		this.setState({ callcenterTriggerModalVisible: false });
	};

	openVipFreespinsModal() {
		this.toggleValueModal(11);
	}

	getTypeColorByID(id) {
		const parsedId = [115, 170, 180, 181, 190, 191].includes(id) ? id : Math.min(id, 99);
		return colorMap[parsedId] || "black";
	}

	isAffiliatePromoCodeLog(item) {
		return Number(item?.type) === 7 && (item?.isAffiliatePromoCode || Number(item?.promoCodeType) === 1);
	}

	getTypeColor(item) {
		if (this.isAffiliatePromoCodeLog(item)) {
			return "purple";
		}

		return this.getTypeColorByID(item.type);
	}

	getTypeByID(item) {
		const allowedTypes = [115, 170, 180, 181, 190, 191];
		const itemType = allowedTypes.includes(item.type) ? item.type : Math.min(parseInt(item.type), 99);

		if (itemType === 7 && this.isAffiliatePromoCodeLog(item)) {
			return "aff promo code";
		}

		if (itemType === 99 && typeof item.gameID !== "undefined") {
			let category = "";
			const game = this.props.game?.data?.find((gameItem) => String(gameItem.gameID) === String(item.gameID));
			const provider = game ? this.props.Interface.getProviderByID(game.providerID) : "";

			if (typeof item.categoryID !== "undefined") {
				category = `(${item.categoryID})`;
			}

			return `${this.props.Interface.getGameByID(item.gameID)} (${provider}) ${category}`.trim() || "bet";
		}

		if (itemType === 69) {
			if (typeof item.gameID !== "undefined" && item.gameID !== 0) {
				return `Tournament (${item.gameID})`;
			} else {
				return typeMap[itemType];
			}
		}

		const typeName = typeMap[itemType] || `undefined (${item.type})`;

		if (itemType === 181 && Array.isArray(item.depositLadderSteps) && item.depositLadderSteps.length > 0) {
			return `${typeName} step ${item.depositLadderSteps.join(", ")}`;
		}

		return typeName;
	}

	handleUnlinkPhone = () => {
		this.props.Interface.unlinkAccount(this.props.playerPage.uid, { phone: true })
			.then((email) => {
				this.props.setPlayerPage({
					...this.props.playerPage,
					data: {
						...this.props.playerPage.data,
						settings: {
							...this.props.playerPage.data.settings,
							phone: "0",
						},
					},
				});
				this.props.Interface.addNoty("Success", email);
			})
			.catch((error) => {});
	};

	handleUnlinkEmail = () => {
		this.props.Interface.unlinkAccount(this.props.playerPage.uid, { email: true })
			.then((email) => {
				this.props.setPlayerPage({
					...this.props.playerPage,
					data: {
						...this.props.playerPage.data,
						settings: {
							...this.props.playerPage.data.settings,
							email: "0",
						},
					},
				});
				this.props.Interface.addNoty("Success", email);
			})
			.catch((error) => {});
	};
	handleUnlinkSocial = (accountType) => {
		this.props.Interface.unlinkAccount(this.props.playerPage.uid, { [accountType]: true })
			.then((social) => {
				this.props.setPlayerPage({
					...this.props.playerPage,
					data: {
						...this.props.playerPage.data,
						social_accounts: {
							...this.props.playerPage.data.social_accounts,
							[accountType]: "0",
						},
					},
				});
				this.props.Interface.addNoty("Success", social);
			})
			.catch((error) => {});
	};

	renderSocialAccount = (name, displayName, url, accountType) => {
		const accountId = parseInt(this.props.playerPage.data.social_accounts[accountType]);
		if (accountId > 1) {
			return (
				<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
					<Box variant="span">
						{accountType === "gl" ? (
							<div
								onClick={() => {
									alert(this.props.playerPage.data.social_accounts.gl);
								}}>
								<Link variant="normal" href={"#"}>
									{displayName} Profile
								</Link>
							</div>
						) : (
							<Link variant="normal" target="_blank" href={url + accountId}>
								{displayName} Profile
							</Link>
						)}
					</Box>

					{(this.props.user.level >= 3 || this.props.user.showColumn.includes("unlink")) && (
						<Button variant="normal" onClick={() => this.handleUnlinkSocial(accountType)}>
							Unlink {name}
						</Button>
					)}
				</SpaceBetween>
			);
		}
		return null;
	};

	resetPassword() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "data", method: "resetPassword" }, { uid: this.props.playerPage.uid })
			.then((password) => {
				this.props.setPlayerPage({ ...this.props.playerPage, data: { ...this.props.playerPage.data, password }, loaded: true });
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	unlinkReferer() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "data", method: "unlinkReferer" }, { uid: this.props.playerPage.uid })
			.then(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, data: { ...this.props.playerPage.data, referer: 0 }, loaded: true });
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	disableTfa() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "data", method: "disableTfa" }, { uid: this.props.playerPage.uid })
			.then(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, data: { ...this.props.playerPage.data, tfa: false }, loaded: true });
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	async addComment(comment, type) {
		const stateKey = type === "public" ? "publicComment" : "privateComment";
		const requestUid = this.props.playerPage.uid;
		const requestProject = this.props.playerPage.data.project;

		this.setState({ [stateKey]: comment });
		this.cancelTimeout(this.commentTimeouts[type]);

		this.commentTimeouts[type] = this.scheduleTimeout(async () => {
			this.commentTimeouts[type] = null;
			try {
				if (type === "public") {
					await this.props.Interface.request("set", { class: "users", module: "data", method: "publicComment" }, { uid: requestUid, comment, project: requestProject });
				} else {
					await this.props.Interface.request("set", { class: "users", module: "data", method: "comment" }, { uid: requestUid, comment, project: requestProject });
				}

				if (!this.unmounted) {
					this.props.setPlayerPage((currentPlayerPage) => {
						if (!currentPlayerPage.show || currentPlayerPage.uid !== requestUid || String(currentPlayerPage.data?.project || 0) !== String(requestProject || 0)) return currentPlayerPage;
						return {
							...currentPlayerPage,
							data: {
								...currentPlayerPage.data,
								[type === "public" ? "publicComment" : "comment"]: comment,
							},
						};
					});
				}
			} catch (error) {
				if (error?.name !== "AbortError") console.error("Error adding comment:", error);
			}
		}, 1000);
	}

	syncBalance() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "wallet", method: "sync" }, { uid: this.props.playerPage.uid })
			.then(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });

				this.setState({
					casinoBalance: {
						loading: false,
						total: parseInt(this.props.playerPage.data.balance),
					},
				});
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	updateVerify(method, vid, project) {
		const requestUid = this.props.playerPage.uid;
		const requestProject = typeof project !== "undefined" ? project : 0;
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "verify", method }, { uid: requestUid, vid, project: requestProject })
			.then(() => {
				this.scheduleTimeout(() => {
					if (this.props.playerPage.uid === requestUid && String(this.props.playerPage.data?.project || 0) === String(requestProject || 0)) {
						this.props.Interface.loadInfo(requestUid, requestProject);
					}
				}, 500);
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	compare(a, b) {
		if (a[this.props.playerPage.sorting.sortingColumn.sortingField] < b[this.props.playerPage.sorting.sortingColumn.sortingField]) {
			return this.props.playerPage.sorting.isDescending ? 1 : -1;
		}

		if (a[this.props.playerPage.sorting.sortingColumn.sortingField] > b[this.props.playerPage.sorting.sortingColumn.sortingField]) {
			return this.props.playerPage.sorting.isDescending ? -1 : 1;
		}

		return 0;
	}

	debounceFilter = (delay = 150) => {
		this.cancelTimeout(this.filterTimeout);
		this.filterTimeout = this.scheduleTimeout(() => {
			this.filterTimeout = null;
			this.filter();
		}, delay);
	};

	debounceGameLogFilter = (delay = 150) => {
		this.cancelTimeout(this.gameLogFilterTimeout);
		this.gameLogFilterTimeout = this.scheduleTimeout(() => {
			this.gameLogFilterTimeout = null;
			this.filterGameLog();
		}, delay);
	};

	filterGameLog() {
		const { playerPage, config, Interface, setPlayerPage } = this.props;
		const { currentSearch } = playerPage.gameLog;
		const { perPage } = config.players.gameLogShort;
		const searchUpper = currentSearch.toUpperCase();
		const hasSearch = currentSearch.length > 0;

		const searchNormalized = currentSearch.toUpperCase().replace(/\s+/g, " ").trim();

		const data = this.state.logCasino.data;

		const filteredData = data.map((item) => {
			const gameSearchNormalized = item.gameSearch.toString().toUpperCase().replace(/\s+/g, " ").trim();

			return {
				...item,
				filtered: !hasSearch || gameSearchNormalized.includes(searchNormalized),
			};
		});

		this.setState(
			(prevState) => ({
				logCasino: {
					...prevState.logCasino,
					data: filteredData,
				},
			}),
			() => {
				const total = Math.ceil(filteredData.filter((item) => item.filtered).length / perPage);

				setPlayerPage({
					...playerPage,
					gameLog: {
						...playerPage.gameLog,
						pagination: { current: 1, total },
					},
				});
			},
		);
	}

	filter() {
		if (this.props.playerPage.log.type < 5) {
			if (!this.props.playerPage.log.currentSearch.length) {
				this.setState({
					filteredData: [],
				});
			} else {
				const currentSearch = this.props.playerPage.log.currentSearch;
				const data = this.props.playerPage.log.data;

				if (!currentSearch || currentSearch.length === 0) {
					this.setState({
						filteredData: [],
					});
					return;
				}

				const filteredData = data.filter((item) => {
					return item.amount.toString().includes(currentSearch) || this.getTypeByID(item).toLowerCase().includes(currentSearch.toLowerCase());
				});

				this.setState({
					filteredData,
				});
			}
		} else
			switch (this.props.playerPage.log.type) {
				case 5:
					if (!this.props.playerPage.log.currentSearch.length) this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(this.props.playerPage.data.initialPromocodes.length / this.props.config.players.log.perPage) }, data: { ...this.props.playerPage.data, promoCodes: cloneDeep(this.props.playerPage.data.initialPromocodes) } });
					else {
						let promoCodes = cloneDeep(this.props.playerPage.data.initialPromocodes);

						promoCodes = promoCodes.filter((item) => {
							if (item.users.toString().indexOf(this.props.playerPage.log.currentSearch) !== -1 || item.count.toString().indexOf(this.props.playerPage.log.currentSearch) !== -1 || item.value.toString().indexOf(this.props.playerPage.log.currentSearch) !== -1) return true;
						});

						this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(promoCodes.length / this.props.config.players.log.perPage) }, data: { ...this.props.playerPage.data, promoCodes } });
					}
					break;
				case 6:
					if (!this.props.playerPage.log.currentSearch.length) this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(this.props.playerPage.data.initialRegData.last.length / this.props.config.players.log.perPage) }, data: { ...this.props.playerPage.data, regdata: cloneDeep(this.props.playerPage.data.initialRegData) } });
					else {
						let regdata = cloneDeep(this.props.playerPage.data.initialRegData);

						regdata.last = regdata.last.filter((item) => {
							if (
								item.ip.toString().toLowerCase().indexOf(this.props.playerPage.log.currentSearch.toLowerCase()) !== -1 ||
								item.os.toString().toLowerCase().indexOf(this.props.playerPage.log.currentSearch.toLowerCase()) !== -1 ||
								item.browser.toString().toLowerCase().indexOf(this.props.playerPage.log.currentSearch.toLowerCase()) !== -1 ||
								(item.timezone || "").toLowerCase().indexOf(this.props.playerPage.log.currentSearch.toLowerCase()) !== -1 ||
								item.region.toString().toLowerCase().indexOf(this.props.playerPage.log.currentSearch.toLowerCase()) !== -1 ||
								item.country.toString().toLowerCase().indexOf(this.props.playerPage.log.currentSearch.toLowerCase()) !== -1
							)
								return true;
						});

						this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(regdata.last.length / this.props.config.players.log.perPage) }, data: { ...this.props.playerPage.data, regdata } });
					}
					break;
				case 7:
					if (!this.props.playerPage.log.currentSearch.length) this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(this.state.userStats.data.length / this.props.config.players.log.perPage) } });
					else {
						let userStats = cloneDeep(this.state.userStats.data);

						userStats = userStats.filter((item) => {
							if (item.gameID.toString().indexOf(this.props.playerPage.log.currentSearch) !== -1) return true;
						});

						this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(userStats.length / this.props.config.players.log.perPage) } });

						this.setState({
							...this.state,
							userStatsSearch: {
								...this.state.userStats,
								data: userStats,
							},
						});
					}
					break;
			}
	}

	setupIntersectionObserver = () => {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.props.Interface.scrollTrack(this.props.playerPage.uid);
						if (this.scrollRef.current) {
							this.observer.unobserve(this.scrollRef.current);
						}
					}
				});
			},
			{ threshold: 0.1 },
		);

		if (this.scrollRef.current) {
			this.observer.observe(this.scrollRef.current);
		} else {
			console.warn("Scroll element not found");
		}
	};

	componentWillUnmount() {
		this.unmounted = true;
		this.profileLoadVersion += 1;
		this.clearPendingTimeouts();

		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
	}

	checkIncorrectPlayerCountry() {
		const countryWhiteList = ["RU"];

		if (this.props.playerPage.data.regdata !== undefined && this.props.playerPage.data.regdata.last !== undefined) {
			for (const last of this.props.playerPage.data.regdata.last) {
				if (countryWhiteList.includes(last.country)) {
					continue;
				} else {
					return { success: true, country: last.country };
				}
			}
		}

		return { success: false, country: "RU" };
	}

	renderFreespinsFilter() {
		return (
			<Grid gridDefinition={[{ colspan: 3 }]}>
				<Select
					filteringType="auto"
					disabled={!this.props.playerPage.freespins?.loaded}
					placeholder="Status"
					selectedOption={
						this.props.playerPage.freespins?.currentFilter && this.props.playerPage.freespins?.currentFilter !== "all"
							? {
									value: this.props.playerPage.freespins.currentFilter,
									label: this.props.playerPage.freespins.currentFilter === "active" ? "Active" : this.props.playerPage.freespins.currentFilter === "completed" ? "Completed" : this.props.playerPage.freespins.currentFilter === "expired" ? "Expired" : "Queue",
							  }
							: null
					}
					onChange={(option) => {
						const newFilter = option.detail.selectedOption?.value || "all";
						this.props.setPlayerPage({
							...this.props.playerPage,
							freespins: {
								...this.props.playerPage.freespins,
								currentFilter: newFilter,
							},
						});
					}}
					options={[
						{ value: "all", label: "All" },
						{ value: "active", label: "Active" },
						{ value: "queue", label: "Queue" },
						{ value: "completed", label: "Completed" },
						{ value: "expired", label: "Expired" },
					]}
				/>
			</Grid>
		);
	}

	renderFreespinsPreferences() {
		return (
			<Preferences
				cancelLabel="Cancel"
				confirmLabel="Confirm"
				pageSizePreference={{
					title: "Freespins per page",
					options: this.props.config.freespins.preferences.perPage,
				}}
				preferences={{ pageSize: this.props.config.freespins.perPage }}
				title="Preferences"
				onConfirm={(preferences) => {
					localStorage.setItem("freespins.perPage", preferences.detail.pageSize);
					this.props.setConfig({
						...this.props.config,
						freespins: {
							...this.props.config.freespins,
							perPage: preferences.detail.pageSize,
						},
					});
					this.props.setPlayerPage({
						...this.props.playerPage,
						freespins: {
							...this.props.playerPage.freespins,
							pagination: {
								...this.props.playerPage.freespins.pagination,
								currentPage: 1,
							},
						},
					});
					setTimeout(() => {
						this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, 1, this.props.playerPage.uid, "active");
					}, 100);
				}}
			/>
		);
	}

	renderFreespinsEmpty() {
		return (
			<Box textAlign="center" color="inherit">
				<div>
					<Box variant="strong" textAlign="center" color="inherit">
						No freespins found
					</Box>
					<Box variant="p" padding={{ bottom: "s" }} color="inherit">
						This player has no freespins yet.
					</Box>
				</div>
			</Box>
		);
	}

	switchTab(tab) {
		switch (tab) {
			case "all5000":
				this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, data: [], min: 0, max: 999999999, limit: 5000, type: 1 } });
				break;
			case "all20000": {
				const uid = this.props.playerPage.uid;
				const project = this.props.playerPage.data.project;
				this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, data: [], min: 0, max: 999999999, limit: 20000, type: 1 } });
				setTimeout(() => {
					const currentPlayerPage = getRecoil(playerPageStore);
					const currentProject = currentPlayerPage.data?.project;
					if (
						currentPlayerPage.uid === uid &&
						typeof currentProject !== "undefined" &&
						currentProject !== null &&
						typeof project !== "undefined" &&
						project !== null &&
						String(currentProject) === String(project) &&
						currentPlayerPage.log.type === 1 &&
						currentPlayerPage.log.min === 0 &&
						currentPlayerPage.log.max === 999999999 &&
						currentPlayerPage.log.limit === 20000
					) {
						this.props.Interface.loadLog(uid, project);
					}
				}, 250);
				break;
			}
			case "big100":
				this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, min: 50, max: 999999999, limit: 100, type: 3 } });
				setTimeout(() => {
					this.props.Interface.loadLog(this.props.playerPage.uid);
				}, 250);
				break;
			case "big5000":
				this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, min: 50, max: 999999999, limit: 5000, type: 4 } });
				setTimeout(() => {
					this.props.Interface.loadLog(this.props.playerPage.uid);
				}, 250);
				break;
			case "promocodes":
				this.props.Interface.loadAllUserPromo(this.props.playerPage.uid, this.props.playerPage.data.project);
				break;
			case "login":
				this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(this.props.playerPage.data.regdata.last.length / this.props.config.players.log.perPage) }, log: { ...this.props.playerPage.log, min: 0, max: 0, limit: 0, type: 6 } });
				break;
			case "actionLog":
				this.props.Interface.loadUserActionLog(this.props.playerPage.uid);
				break;
			case "stats":
				this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(this.state.userStats.data.length / this.props.config.players.log.perPage) }, log: { ...this.props.playerPage.log, min: 0, max: 0, limit: 0, type: 7 } });
				break;

			case "freespins": {
				this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, 1, this.props.playerPage.uid, "active");
				break;
			}

			case "bonuses": {
				if (this.props.playerPage.bonuses.fortuneWheel.data.length === 0) {
					setTimeout(() => {
						this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid);
					}, 250);
				}
				break;
			}
		}
	}

	renderActionLogDetails(item) {
		let detailsPayload = item.details;

		if (typeof item.data !== "undefined") {
			if (typeof item.data === "string") {
				try {
					detailsPayload = JSON.parse(item.data);
				} catch (error) {
					return item.data;
				}
			} else {
				detailsPayload = item.data;
			}
		}

		if (item.action === "currencyChange") {
			const from = typeof detailsPayload?.from !== "undefined" ? detailsPayload.from : item.from;
			const to = typeof detailsPayload?.to !== "undefined" ? detailsPayload.to : item.to;

			if (typeof from !== "undefined" || typeof to !== "undefined") {
				return `${typeof from !== "undefined" ? from : ""} → ${typeof to !== "undefined" ? to : ""}`.trim();
			}
		}

		if (detailsPayload) {
			if (typeof detailsPayload === "string") {
				return detailsPayload;
			}

			if (typeof detailsPayload === "object") {
				if (typeof detailsPayload.text === "string") {
					return detailsPayload.text;
				}

				return JSON.stringify(detailsPayload);
			}
		}

		return "-";
	}

	getEconomicScore() {
		const deposits = this.state.paymentsStats.statsDeposit.deposits || { total: 0, count: 0 };
		const withdrawals = this.state.paymentsStats.statsWithdrawals.withdrawals || { total: 0, count: 0 };
		let score = deposits.total - withdrawals.total;

		if (deposits.count) {
			score *= deposits.count;
			score *= deposits.total / deposits.count / 100;
		}

		if (withdrawals.count && withdrawals.total) {
			score /= withdrawals.count;
			score /= withdrawals.total / withdrawals.count / 100;
		}

		score = score / 100;

		return { score: score.toFixed(4), color: score < 0 ? "red" : score < 5 ? "orange" : score < 50 ? "purple" : "green" };
	}

	renderWelcomeBonusSection() {
		const welcomeBonusState = this.state.welcomeBonus;
		const currentProject = this.props.playerPage.data.project;

		if (
			welcomeBonusState.uid !== this.props.playerPage.uid ||
			String(welcomeBonusState.project || 0) !== String(currentProject || 0)
		) {
			return null;
		}

		const welcomeBonus = welcomeBonusState.data;

		if (welcomeBonus?.participant !== true) return null;

		const steps = Array.isArray(welcomeBonus.steps) ? welcomeBonus.steps : [];
		const claimedSteps = Array.isArray(welcomeBonus.claimedSteps) ? welcomeBonus.claimedSteps : [];
		const allowedCountries = Array.isArray(welcomeBonus.allowedCountries)
			? welcomeBonus.allowedCountries.length === 0
				? "Unrestricted"
				: welcomeBonus.allowedCountries.filter((country) => typeof country === "string" && country.trim()).join(", ") || "—"
			: "—";
		const assignedAt = welcomeBonus.assignedAt == null ? null : new Date(welcomeBonus.assignedAt);
		const assignedAtLabel = assignedAt && Number.isFinite(assignedAt.getTime()) ? `${assignedAt.toLocaleDateString()} ${assignedAt.toLocaleTimeString()}` : "—";
		const formatNumber = (value) => {
			if (value === null || typeof value === "undefined" || (typeof value === "string" && !value.trim())) return null;

			const number = Number(value);

			return Number.isFinite(number) ? new Intl.NumberFormat("ru-RU").format(number) : null;
		};
		const formatMoney = (value) => {
			if (value === null || typeof value === "undefined" || value === "") return "—";

			const formattedValue = formatNumber(value);
			return formattedValue === null ? "—" : `${formattedValue} RUB.`;
		};
		const formatCondition = (value) => {
			if (value === null || typeof value === "undefined" || value === "") return "—";
			if (typeof value === "number") return Number.isFinite(value) ? value : "—";
			if (typeof value !== "string") return "—";

			const normalizedValue = value.trim();
			return normalizedValue && normalizedValue !== "NaN" && normalizedValue !== "undefined" ? normalizedValue : "—";
		};
		const formatReward = (step) => {
			const rewardType = step?.rewardType === "freespins" || step?.rewardType === "both" ? step.rewardType : "deposit";
			const rewards = [];

			if (rewardType !== "freespins") {
				const bonusPercent = formatNumber(step?.bonusPercent);
				rewards.push(bonusPercent === null ? "Deposit bonus" : `${bonusPercent}% deposit bonus`);
			}

			if (rewardType !== "deposit") {
				const freespinsTotal = formatNumber(step?.freespins?.totalSpins);
				rewards.push(freespinsTotal === null ? "FS" : `${freespinsTotal} FS`);
			}

			return rewards.join(" + ") || "—";
		};
		const renderKeyValue = (label, value) => (
			<Box>
				<Box variant="awsui-key-label">{label}</Box>
				<Box>{value}</Box>
			</Box>
		);
		const trafficComparison =
			welcomeBonus.trafficGeoMismatch === true ? (
				<Badge color="red">Mismatch</Badge>
			) : welcomeBonus.trafficGeoMismatch === false ? (
				<Badge color="green">Match</Badge>
			) : (
				<Badge color="grey">Unknown</Badge>
			);
		const assignmentStatus = welcomeBonus.available === false || welcomeBonus.status === "incomplete" ? <Badge color="red">Unavailable</Badge> : null;
		const sourceLadderId = formatCondition(welcomeBonus.sourceLadderId);
		const campaignName = formatCondition(welcomeBonus.name);
		const campaignId = sourceLadderId === "—" ? null : `#${sourceLadderId}`;
		const campaign = (
			<>
				{campaignName}{" "}
				{campaignId && campaignName !== campaignId && (
					<Box variant="span" color="text-body-secondary">
						{campaignId}
					</Box>
				)}
			</>
		);
		const participantStatus = (
			<SpaceBetween direction="horizontal" size="xs">
				<Badge color="green">Participant</Badge>
				{assignmentStatus}
			</SpaceBetween>
		);

		return (
			<Box margin={{ top: "m" }}>
				<Container header={<Header variant="h3">Welcome Bonus</Header>}>
					<SpaceBetween size="m">
						<SpaceBetween size="s">
							<Header variant="h3">Summary</Header>
							<Grid
								gridDefinition={[
									{ colspan: { default: 12, xs: 6, m: 3 } },
									{ colspan: { default: 12, xs: 6, m: 3 } },
									{ colspan: { default: 12, xs: 6, m: 3 } },
									{ colspan: { default: 12, xs: 6, m: 3 } },
								]}>
								{renderKeyValue("Status", participantStatus)}
								{renderKeyValue("Campaign", campaign)}
								{renderKeyValue("Assigned", assignedAtLabel)}
								{renderKeyValue("Progress", `${claimedSteps.length} of ${steps.length} steps`)}
							</Grid>
						</SpaceBetween>
						<SpaceBetween size="s">
							<Header variant="h3">Geo verification</Header>
							<Grid
								gridDefinition={[
									{ colspan: { default: 12, xs: 6, m: 3 } },
									{ colspan: { default: 12, xs: 6, m: 3 } },
									{ colspan: { default: 12, xs: 6, m: 3 } },
									{ colspan: { default: 12, xs: 6, m: 3 } },
								]}>
								{renderKeyValue("Allowed countries", allowedCountries)}
								{renderKeyValue("Assignment IP country", formatCondition(welcomeBonus.ipCountry))}
								{renderKeyValue("Confirmed phone country", formatCondition(welcomeBonus.phoneCountry))}
								{renderKeyValue("IP / phone comparison", trafficComparison)}
							</Grid>
						</SpaceBetween>
						<Table
							wrapLines={true}
							trackBy="depositNumber"
							items={steps}
							empty="No campaign conditions"
							header={
								<Header variant="h3" counter={`(${steps.length})`}>
									Conditions
								</Header>
							}
							columnDefinitions={[
								{ id: "depositNumber", header: "Step", cell: (item) => formatCondition(item?.depositNumber) },
								{
									id: "reward",
									header: "Reward",
									cell: (item) => formatReward(item),
								},
								{ id: "minDeposit", header: "Minimum deposit", cell: (item) => formatMoney(item?.minDeposit) },
								{ id: "wager", header: "Wager", cell: (item) => (item?.rewardType === "freespins" ? "—" : formatCondition(item?.wager)) },
								{ id: "cap", header: "Cap", cell: (item) => (item?.rewardType === "freespins" ? "—" : formatCondition(item?.cap)) },
							]}
						/>
					</SpaceBetween>
				</Container>
			</Box>
		);
	}

	switchLogExpand(expandableItem, expand) {
		let log = this.props.playerPage.log.data;
		const requestUid = this.props.playerPage.uid;
		const requestPlayerId = this.props.playerPage.data?.id;
		const requestProject = this.props.playerPage.data?.project;
		const isCurrentPlayer = () => !this.unmounted && this.props.playerPage.show && this.props.playerPage.uid === requestUid && this.props.playerPage.data?.id === requestPlayerId && String(this.props.playerPage.data?.project || 0) === String(requestProject || 0);

		log = log.filter((logItem) => {
			if (logItem.mainItem === expandableItem.id && logItem.id !== expandableItem.id) {
				return false;
			}
			return true;
		});

		log = log.map((item) => {
			const uid = this.props.playerPage.uid;
			const type = expandableItem.type;
			const date = expandableItem.date;

			if (item.id === expandableItem.id && item.type === 99) {
				this.props.Interface.loadLogGameDetail(uid, type, date, this.props.playerPage.data.project, item.gameID, item.operations);
			} else if (item.id === expandableItem.id || (!expand && item.mainItem === expandableItem.id)) {
				const updatedItem = { ...item, expanded: expand, filtered: true, firstOperation: false, isLoading: expand && !item.detailed_operations };

				if (expand && !updatedItem.detailed_operations) {
					this.props.setPlayerPage({
						...this.props.playerPage,
						log: { ...this.props.playerPage.log, data: log },
					});

					this.props.Interface.loadLogDetail(uid, type, date, this.props.playerPage.data.project, item.gameID)
						.then((data) => {
							if (!isCurrentPlayer()) return;
							const logDetails = data.map((inner, index) => {
								const amount = inner.amount;
								const from = inner.from;
								const to = inner.to;

								return {
									id: `${expandableItem.id}-op-${index}`,
									type: inner.type || item.type,
									date: new Date(inner.date || expandableItem.date),
									amount,
									filtered: true,
									mainItem: expandableItem.id,
									from,
									to,
									gameID: inner.gameID,
									promoCodeType: inner.promoCodeType,
									isAffiliatePromoCode: inner.isAffiliatePromoCode,
								};
							});

							const mainItemIndex = log.findIndex((logItem) => logItem.id === expandableItem.id);

							let newLog = log.map((logItem) => {
								if (logItem.id === updatedItem.id) {
									return { ...logItem, isLoading: false };
								}
								return logItem;
							});

							if (mainItemIndex !== -1) {
								newLog.splice(mainItemIndex + 1, 0, ...logDetails);
							} else {
								newLog = [...newLog, ...logDetails];
							}

							const filteredLog = newLog.filter((logItem) => {
								if (logItem.mainItem === expandableItem.id && logItem.id !== expandableItem.id && !logItem.id.includes("-op-")) {
									return false;
								}
								return true;
							});

							const total = Math.ceil(
								filteredLog.filter((item) => {
									return (item.filtered || (typeof item.expanded !== "undefined" && item.expanded)) && (typeof item.mainItem === "undefined" || this.isItemOpen(item.mainItem));
								}).length / this.props.config.players.log.perPage,
							);

							this.props.setPlayerPage({
								...this.props.playerPage,
								log: { ...this.props.playerPage.log, data: filteredLog },
								pagination: {
									current: this.props.playerPage.pagination.current < total ? this.props.playerPage.pagination.current : 1,
									total,
								},
							});
						})
						.catch((error) => {
							if (!isCurrentPlayer()) return;
							const newLog = log.map((logItem) => {
								if (logItem.id === updatedItem.id) {
									return { ...logItem, isLoading: false };
								}
								return logItem;
							});

							const filteredLog = newLog.filter((logItem) => {
								if (logItem.mainItem === expandableItem.id && logItem.id !== expandableItem.id && !logItem.id.includes("-op-")) {
									return false;
								}
								return true;
							});

							this.props.setPlayerPage({
								...this.props.playerPage,
								log: { ...this.props.playerPage.log, data: filteredLog },
							});
						});
				}

				return updatedItem;
			}
			return item;
		});

		const filteredLogInitial = log.filter((logItem) => {
			if (logItem.mainItem === expandableItem.id && logItem.id !== expandableItem.id) {
				return false;
			}
			return true;
		});

		this.props.setPlayerPage({
			...this.props.playerPage,
			log: { ...this.props.playerPage.log, data: filteredLogInitial },
		});
	}
	isItemOpen(id) {
		let expanded = false;

		this.props.playerPage.log.data.forEach((item) => {
			if (item.id === id) {
				expanded = item.expanded;
			}
		});

		return expanded;
	}

	render() {
		const incorrectCountry = this.checkIncorrectPlayerCountry();
		const economicScore = this.getEconomicScore();
		const project = this.props.playerPage.data.project;
		const hasWriteOffAccess = Number(this.props.user.level) >= 6 || this.props.user.showColumn.includes("balanceDownGrade");
		const hasBalanceCreditAccess = Number(this.props.user.level) >= 6 || this.props.user.showColumn.includes("balanceCredit");
		const canChangeCurrentBalance = (!project || this.props.user.brand.id === project) && (hasWriteOffAccess || hasBalanceCreditAccess);
		const multiWithdrawalsPageSize = this.getProjectPageSize(this.props.config.players.multiWithdrawals, project, "players.multiWithdrawals.perPageByProject");
		const multiPlayersPageSize = this.getProjectPageSize(this.props.config.players.multiPlayers, project, "players.multiPlayers.perPageByProject");
		const sessionsPageSize = this.getProjectPageSize(this.props.config.players.sessions, project, "players.sessions.perPageByProject");

		return (
			<>
				<Modal
					className={this.props.styles["modalWithBrand"]}
					size="max"
					visible={this.props.playerPage.show}
					onDismiss={() => {
						this.props.resetUserActionLog();
						this.props.setPlayerPage({
							...this.props.playerPage,
							show: false,
							data: {},
							loaded: false,
							bonuses: {
								fortuneWheel: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 0,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								sweepstakesActive: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									currentSearchType: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 1,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								sweepstakesHistory: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									currentSearchType: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 1,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								achievementsActive: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									currentSearchType: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 1,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								achievementsHistory: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									currentSearchType: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 1,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								allPromocode: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentOption: {},
									currentSearchType: "",
									currentSearch: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 0,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								promocode: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 0,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
								rain: {
									sorting: {
										sortingColumn: {
											order: "",
											column: "",
										},
										isDescending: false,
									},
									currentSearch: "",
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
									pagination: {
										current: 0,
										total: 0,
										count: 0,
									},
									data: [],
									loaded: false,
								},
							},
							log: { ...this.props.playerPage.log, loaded: false, data: [] },
						});

						this.props.setMultiWithdrawal({
							...this.props.multiWithdrawal,
							loaded: false,
							multiAccount: {
								withdrawals: [],
								multiPlayers: [],
							},
							multiWithdrawals: {
								pagination: {
									current: 0,
									total: 0,
									count: 0,
								},
							},
							multiPlayers: {
								pagination: {
									current: 0,
									total: 0,
									count: 0,
								},
							},
							error: {
								text: "",
								code: 0,
								timestamp: 0,
							},
						});

						this.profileLoadVersion += 1;
						this.clearPendingTimeouts();
						this.props.resetPlayerPage();
						this.props.resetMultiWithdrawal();
					}}
					header={
						<Header
							description={typeof this.props.playerPage.data.settings !== "undefined" ? this.props.playerPage.data.settings.name : ""}
							counter={"(" + this.props.playerPage.uid + ")"}
							children="User profile"
							actions={this.props.playerPage.data.project ? <img src={document.body.classList[0] === "awsui-dark-mode" ? this.props.user.brandsList[this.props.playerPage.data.project - 1].logoDark : this.props.user.brandsList[this.props.playerPage.data.project - 1].logo}></img> : <></>}
						/>
					}
					children={
						<>
							{!this.props.playerPage.loaded ? (
								<Box padding={{ top: "xxxl", bottom: "xxxl" }} children={<Box textAlign="center" padding={{ top: "xxxl", bottom: "xxxl" }} margin={{ top: "xxxl", bottom: "xxxl" }} children={<Spinner size="big" />} />} />
							) : (
								<>
									<Box
										children={
											<Grid
												gridDefinition={[{ colspan: { default: 12, m: 6 } }, { colspan: { default: 12, m: 6 } }]}
												children={
													<>
														<Box
															children={
																<>
																	<Grid
																		gridDefinition={[{ colspan: 12 }, { colspan: 12 }, { colspan: 12 }, { colspan: 12 }, { colspan: 12 }, { colspan: 12 }, { colspan: 12 }, { colspan: 12 }]}
																		children={
																			<>
																				<Container
																					children={
																						<Grid
																							disableGutters={true}
																							gridDefinition={[{ colspan: { default: 12, xxs: 12, xs: 3, s: 3, m: 3, xl: 3 } }, { colspan: { default: 12, xxs: 12, xs: 9, s: 9, m: 9, xl: 9 } }]}
																							children={
																								<>
																									<Container
																										className={this.props.styles["grid80box"]}
																										children={
																											<>
																												<Box className={this.props.styles["flexContainer"]}>
																													<Box className={this.props.styles["box"]}>
																														<Avatar maxInitials={2} name={typeof this.props.playerPage.data.settings !== "undefined" ? this.props.playerPage.data.settings.name : ""} size="125" />
																													</Box>
																													<Box className={this.props.styles["box"]} margin={{ top: "m" }}>
																														{this.props.user.level >= 6 ? <>{!this.state.paymentsStats.statsDeposit.loading && !this.state.paymentsStats.statsWithdrawals.loading ? <Box variant="span" children={<Badge className={[this.props.styles["badge"], this.props.styles["eScore"], this.props.styles[economicScore.color]].join(" ")} children={economicScore.score} />} /> : <Spinner size="big" />}</> : <></>}
																													</Box>
																												</Box>
																											</>
																										}
																									/>
																									<SpaceBetween className={this.props.styles["playerItems"]} size="m" direction="vertical">
																										<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																											<Box variant="span">
																												Verified: <b>{this.getVerifyStatusLabel()}</b>
																											</Box>
																										</SpaceBetween>
																										<SpaceBetween className={[this.props.styles["item"]].join(" ")} direction="horizontal">
																											<Box variant="span">
																												Self-Exclusion: <b>{this.getSelfExclusionLabel()}</b>
																											</Box>
																										</SpaceBetween>
																										<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																											<Box variant="span">
																												Timezone: <b>{this.props.playerPage.data.settings && this.props.playerPage.data.settings.timezone ? this.props.playerPage.data.settings.timezone : "—"}</b>
																											</Box>
																										</SpaceBetween>
																										{typeof this.props.playerPage.data.settings !== "undefined" && typeof this.props.playerPage.data.settings.email !== "undefined" && this.props.playerPage.data.settings.email !== "0" ? (
																											<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																												<Box variant="span">
																													Mail: <b>{this.props.playerPage.data.settings.email ? this.props.playerPage.data.settings.email : "not set"}</b>
																												</Box>
																												{(this.props.user.level >= 3 || this.props.user.showColumn.includes("unlink")) && this.props.playerPage.data.settings.email && <Button className={this.props.styles["py0"]} onClick={() => this.handleUnlinkEmail()} variant="normal" children="Unlink" />}
																											</SpaceBetween>
																										) : (
																											<></>
																										)}
																										{typeof this.props.playerPage.data.settings !== "undefined" && typeof this.props.playerPage.data.settings.phone !== "undefined" && this.props.playerPage.data.settings.phone !== "0" ? (
																											<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																												<Box variant="span">
																													Phone: <b>{this.props.playerPage.data.settings.phone ? this.props.playerPage.data.settings.phone : "not set"}</b>
																												</Box>
																												{(this.props.user.level >= 3 || this.props.user.showColumn.includes("unlink")) && this.props.playerPage.data.settings.phone && <Button className={this.props.styles["py0"]} onClick={() => this.handleUnlinkPhone()} variant="normal" children="Unlink" />}
																											</SpaceBetween>
																										) : (
																											<></>
																										)}
																										<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																											<Box variant="span">
																												Current balance: <b>{new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.balance)} RUB.</b>
																											</Box>
																											{canChangeCurrentBalance ? <Button className={this.props.styles["py0"]} iconName="edit" onClick={() => this.toggleValueModal(0)} variant="normal" children="Change" /> : <></>}
																											{(!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) && (this.props.user.level >= 6 || this.props.user.showColumn.includes("balance")) ? <Button className={this.props.styles["py0"]} iconName="edit" onClick={() => this.toggleValueModal(1)} variant="normal" children="Change" /> : <></>}
																										</SpaceBetween>
																										{this.props.playerPage.data.casinoID ? (
																											<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																												<Box variant="span">Casino balance: {this.state.casinoBalance.loading ? <Spinner size={"big"} /> : <b className={this.props.playerPage.data.balance !== this.state.casinoBalance.total ? "text-danger" : ""}>{new Intl.NumberFormat("ru-RU").format(this.state.casinoBalance.total)} RUB.</b>}</Box>
																												{!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project ? <Button className={this.props.styles["py0"]} iconName="status-pending" onClick={() => this.syncBalance()} variant="normal" children="Sync" /> : <></>}
																											</SpaceBetween>
																										) : (
																											<></>
																										)}
																										<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																											<Box variant="span">
																												Partner balance: <b>{new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.balance_r)} RUB.</b>
																											</Box>
																											{(!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) && (this.props.user.level >= 6 || this.props.user.showColumn.includes("balancePartner")) ? <Button className={this.props.styles["py0"]} iconName="edit" onClick={() => this.toggleValueModal(2)} variant="normal" children="Change" /> : <></>}
																										</SpaceBetween>
																										<SpaceBetween className={[this.props.styles["item"]].join(" ")} direction="horizontal">
																											<Box variant="span">
																												Balance rakeback: <b>{new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.balance_rakeback)} RUB.</b>
																											</Box>
																											{(!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) && (this.props.user.level >= 6 || this.props.user.showColumn.includes("balanceRakeback")) ? <Button className={this.props.styles["py0"]} iconName="edit" onClick={() => this.toggleValueModal(3)} variant="normal" children="Change" /> : <></>}
																										</SpaceBetween>
																										<SpaceBetween className={[this.props.styles["item"], this.props.styles["noHr"]].join(" ")} direction="horizontal">
																											<Box variant="span">
																												Tickets: <b>{this.props.playerPage.data.tickets}</b>
																											</Box>
																										</SpaceBetween>
																									</SpaceBetween>
																								</>
																							}
																						/>
																					}
																				/>
																				<Container>
																					<SpaceBetween className={this.props.styles["playerItems"]} size="m" direction="vertical">
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								Login: <b>{this.props.playerPage.data.login.length > 1 ? this.props.playerPage.data.login : "not set"}</b>
																							</Box>
																							<Button
																								className={this.props.styles["py0"]}
																								iconName="copy"
																								variant="normal"
																								onClick={() => {
																									navigator.clipboard.writeText(this.props.playerPage.data.login.length > 1 ? this.props.playerPage.data.login : "");
																								}}
																								children="Copy"
																							/>
																						</SpaceBetween>
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								CasinoID: <b>{this.props.playerPage.data.casinoID ? this.props.playerPage.data.casinoID : "not set"}</b>
																							</Box>
																							<Button
																								className={this.props.styles["py0"]}
																								iconName="copy"
																								variant="normal"
																								onClick={() => {
																									navigator.clipboard.writeText(this.props.playerPage.data.casinoID ? this.props.playerPage.data.casinoID : "");
																								}}
																								children="Copy"
																							/>
																						</SpaceBetween>
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								Reg date: <b>{new Date(this.props.playerPage.data.regdata.date).toLocaleDateString() + " " + new Date(this.props.playerPage.data.regdata.date).toLocaleTimeString()}</b>
																							</Box>
																						</SpaceBetween>
																						{this.props.user.level >= 6 || this.props.user.showColumn.includes("firstDeposit") ? (
																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									{" "}
																									First deposit:
																									{!this.state.paymentsStats.statsDeposit.loading ? <b> {typeof this.state.paymentsStats.statsDeposit.firstDeposit !== "undefined" ? new Date(this.state.paymentsStats.statsDeposit.firstDeposit).toLocaleDateString() + " " + new Date(this.state.paymentsStats.statsDeposit.firstDeposit).toLocaleTimeString() : "no"}</b> : <Spinner size="big" />}
																								</Box>
																							</SpaceBetween>
																						) : (
																							<></>
																						)}
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								IP address: <b>{this.props.playerPage.data.regdata.ip}</b>
																							</Box>
																							<Button
																								className={this.props.styles["py0"]}
																								iconName="copy"
																								variant="normal"
																								onClick={() => {
																									navigator.clipboard.writeText(this.props.playerPage.data.regdata.ip);
																								}}
																								children="Copy"
																							/>
																						</SpaceBetween>
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								Refer:{" "}
																								<b>
																									{this.props.playerPage.data.referer ? (
																										<Link
																											onFollow={() => {
																												this.props.Interface.loadInfo(this.props.playerPage.data.referer, this.props.playerPage.data.project);
																											}}
																											children={this.props.playerPage.data.referer}
																										/>
																									) : (
																										"none"
																									)}
																								</b>
																							</Box>
																							{this.props.playerPage.data.referer ? (
																								<Button
																									className={this.props.styles["py0"]}
																									iconName="copy"
																									variant="normal"
																									onClick={() => {
																										navigator.clipboard.writeText(this.props.playerPage.data.referer);
																									}}
																									children="Copy"
																								/>
																							) : (
																								<></>
																							)}
																						</SpaceBetween>
																						<SpaceBetween className={[this.props.styles["item"]].join(" ")} direction="horizontal">
																							<Box variant="span">
																								TFA: <b>{this.props.playerPage.data.tfa ? "Enabled" : "Disabled"}</b>
																							</Box>
																						</SpaceBetween>
																						{this.props.playerPage.data.password ? (
																							<SpaceBetween className={[this.props.styles["item"], this.props.styles["noHr"]].join(" ")} direction="horizontal">
																								<Box variant="span">
																									New password: <b style={{ color: "red" }}>{this.props.playerPage.data.password}</b>
																								</Box>
																							</SpaceBetween>
																						) : (
																							<></>
																						)}
																					</SpaceBetween>
																				</Container>
																				<Container>
																					<SpaceBetween className={this.props.styles["playerItems"]} size="m" direction="vertical">
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								Bets: <b>{new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.stats.bets / 100)} RUB.</b>
																							</Box>
																						</SpaceBetween>
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								Wins: <b>{new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.stats.wins / 100)} RUB.</b>
																							</Box>
																						</SpaceBetween>
																					</SpaceBetween>
																				</Container>
																				{(this.props.user.showColumn.includes("showPlayerRules") || this.props.user.level >= 6) && (
																					<Container
																						header={
																							<Header variant="h3" description="Internal fact tags">
																								Fact
																							</Header>
																						}>
																						{(this.props.user.showColumn.includes("showPlayerRules") || this.props.user.level >= 6) && (
																							<SpaceBetween className={[this.props.styles["item"], !this.props.playerPage.data.password ? this.props.styles["noHr"] : ""].join(" ")} direction="horizontal">
																								{!this.state.rules.loading ? (
																									<>
																										{this.state.rules.data.length > 0 ? (
																											this.state.rules.data.map((el, index, arr) => this.renderRuleTag(el, "fact-tag", index, arr.length))
																										) : (
																											<>
																												<Box variant="span">Fact not found</Box>
																											</>
																										)}
																									</>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>
																						)}
																					</Container>
																				)}
																				{(this.props.user.showColumn.includes("showPlayerRules") || this.props.user.level >= 6) && (
																					<Container
																						header={
																							<Header variant="h3" description="Prediction tags">
																								Prediction
																							</Header>
																						}>
																						{(this.props.user.showColumn.includes("showPlayerRules") || this.props.user.level >= 6) && (
																							<SpaceBetween className={[this.props.styles["item"], !this.props.playerPage.data.password ? this.props.styles["noHr"] : ""].join(" ")} direction="horizontal">
																								{!this.state.externalRules.loading ? (
																									<>
																										{this.state.externalRules.data.length > 0 ? (
																											this.state.externalRules.data.map((el, index, arr) => this.renderRuleTag(el, "prediction-tag", index, arr.length))
																										) : (
																											<>
																												<Box variant="span">Prediction tags not found</Box>
																											</>
																										)}
																									</>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>
																						)}
																					</Container>
																				)}
																				{(this.props.user.showColumn.includes("statsByDays") || this.props.user.level >= 6) && (
																					<Container>
																						<SpaceBetween className={this.props.styles["playerItems"]} size="m" direction="vertical">
																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									<b>Deposits last 30 days</b>:
																								</Box>
																								{!this.state.paymentsStats.statsDeposit.loading ? (
																									<Badge
																										className={[this.props.styles["badge"], this.state.paymentsStats.statsDeposit.depositsMonth.total > 0 ? this.props.styles["green"] : this.props.styles["dark"]].join(" ")}
																										children={
																											<>
																												{this.state.paymentsStats.statsDeposit.depositsMonth.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsDeposit.depositsMonth.total) : "0"} RUB. ({this.state.paymentsStats.statsDeposit.depositsMonth.count})
																											</>
																										}
																									/>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>

																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									<b>Withdrawals last 30 days</b>:
																								</Box>
																								{!this.state.paymentsStats.statsWithdrawals.loading ? (
																									<Badge
																										className={[this.props.styles["badge"], this.state.paymentsStats.statsWithdrawals.withdrawalsMonth.total > 0 ? this.props.styles["red"] : this.props.styles["dark"]].join(" ")}
																										children={
																											<>
																												{this.state.paymentsStats.statsWithdrawals.withdrawalsMonth.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsWithdrawals.withdrawalsMonth.total) : "0"} RUB. ({this.state.paymentsStats.statsWithdrawals.withdrawalsMonth.count})
																											</>
																										}
																									/>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>

																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									<b>Deposits last 7 days</b>:
																								</Box>

																								{!this.state.paymentsStats.statsDeposit.loading ? (
																									<Badge
																										className={[this.props.styles["badge"], this.state.paymentsStats.statsDeposit.depositsWeek.total > 0 ? this.props.styles["green"] : this.props.styles["dark"]].join(" ")}
																										children={
																											<>
																												{this.state.paymentsStats.statsDeposit.depositsWeek.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsDeposit.depositsWeek.total) : "0"} RUB. ({this.state.paymentsStats.statsDeposit.depositsWeek.count})
																											</>
																										}
																									/>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>

																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									<b>Withdrawals last 7 days</b>:
																								</Box>
																								{!this.state.paymentsStats.statsWithdrawals.loading ? (
																									<Badge
																										className={[this.props.styles["badge"], this.state.paymentsStats.statsWithdrawals.withdrawalsWeek.total > 0 ? this.props.styles["red"] : this.props.styles["dark"]].join(" ")}
																										children={
																											<>
																												{this.state.paymentsStats.statsWithdrawals.withdrawalsWeek.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsWithdrawals.withdrawalsWeek.total) : "0"} RUB. ({this.state.paymentsStats.statsWithdrawals.withdrawalsWeek.count})
																											</>
																										}
																									/>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>

																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									<b>Deposits last 24 hours</b>:
																								</Box>

																								{!this.state.paymentsStats.statsDeposit.loading ? (
																									<Badge
																										className={[this.props.styles["badge"], this.state.paymentsStats.statsDeposit.depositsDay.total > 0 ? this.props.styles["green"] : this.props.styles["dark"]].join(" ")}
																										children={
																											<>
																												{this.state.paymentsStats.statsDeposit.depositsDay.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsDeposit.depositsDay.total) : "0"} RUB. ({this.state.paymentsStats.statsDeposit.depositsDay.count})
																											</>
																										}
																									/>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>

																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span">
																									<b>Withdrawals last 24 hours</b>:
																								</Box>
																								{!this.state.paymentsStats.statsWithdrawals.loading ? (
																									<Badge
																										className={[this.props.styles["badge"], this.state.paymentsStats.statsWithdrawals.withdrawalsDay.total > 0 ? this.props.styles["red"] : this.props.styles["dark"]].join(" ")}
																										children={
																											<>
																												{this.state.paymentsStats.statsWithdrawals.withdrawalsDay.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsWithdrawals.withdrawalsDay.total) : "0"} RUB. ({this.state.paymentsStats.statsWithdrawals.withdrawalsDay.count})
																											</>
																										}
																									/>
																								) : (
																									<Spinner size="big" />
																								)}
																							</SpaceBetween>
																						</SpaceBetween>
																					</Container>
																				)}

																				{(this.props.user.showColumn.includes("showPartnerMetadata") || this.props.user.level >= 6) && <PlayerProfilePartnerMetadataSection Interface={this.props.Interface} uid={this.props.playerPage?.uid} project={this.props.playerPage?.data?.project} user={this.props.user} />}
																				<Box
																					margin={{ vertical: "m" }}
																					children={
																						<>
																							<SpaceBetween size="m" className={this.props.styles["item"]} direction="horizontal">
																								{(this.props.user.level >= 6 || this.props.user.showColumn.includes("resetPassword")) && (!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) ? (
																									<Box
																										variant="span"
																										children={
																											<>
																												<Link
																													variant="normal"
																													onFollow={() => {
																														this.resetPassword();
																													}}
																													href="#"
																													children="Reset password"
																												/>
																											</>
																										}
																									/>
																								) : (
																									<></>
																								)}

																								{(this.props.user.level >= 2 || this.props.user.showColumn.includes("givePromo")) && (!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) ? (
																									<Box
																										variant="span"
																										children={
																											<>
																												<Link variant="normal" onFollow={() => this.toggleValueModal(6)} href="#" children="Give promo" />
																											</>
																										}
																									/>
																								) : (
																									<></>
																								)}

																								{(this.props.user.level >= 6 || this.props.user.showColumn.includes("unlinkReferer")) && (!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) ? (
																									<Box variant="span">
																										<Link
																											variant="normal"
																											onFollow={() => {
																												this.unlinkReferer(this.props.playerPage.data.id);
																											}}
																											href="#">
																											Unlink referer
																										</Link>
																									</Box>
																								) : (
																									<></>
																								)}

																								{(this.props.user.level >= 6 || this.props.user.showColumn.includes("createFreespins")) && (!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) ? (
																									<Box variant="span">
																										<Link
																											variant="normal"
																											onFollow={() => {
																												this.openCreateFreespinsModal();
																											}}
																											href="#">
																											Create Freespins
																										</Link>
																									</Box>
																								) : (
																									<></>
																								)}

																								{(this.props.user.level >= 6 || this.props.user.showColumn.includes("callcenterTrigger")) && (!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) ? (
																									<Box variant="span">
																										<Link
																											variant="normal"
																											onFollow={() => {
																												this.openCallcenterTriggerModal();
																											}}
																											href="#">
																											Callcenter Trigger
																										</Link>
																									</Box>
																								) : (
																									<></>
																								)}

																								{(this.props.user.level >= 6 || this.props.user.showColumn.includes("vipGrantFreespins")) && (!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) ? (
																									<Box variant="span">
																										<Link
																											variant="normal"
																											onFollow={() => {
																												this.openVipFreespinsModal();
																											}}
																											href="#">
																											VIP FS
																										</Link>
																									</Box>
																								) : (
																									<></>
																								)}

																								{(!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project) && this.props.playerPage.data.tfa ? (
																									<Box
																										variant="span"
																										children={
																											<>
																												<Link
																													variant="normal"
																													onFollow={() => {
																														this.disableTfa();
																													}}
																													href="#"
																													children="Disable TFA"
																												/>
																											</>
																										}
																									/>
																								) : (
																									<></>
																								)}
																							</SpaceBetween>
																						</>
																					}
																				/>
																			</>
																		}
																	/>
																</>
															}
														/>
														<Box
															children={
																<>
																	<Container>
																		<SpaceBetween className={this.props.styles["playerItems"]} size="m" direction="vertical">
																			{typeof this.state.paymentsStats.statsDeposit.deposits !== "undefined" || this.props.user.showColumn.includes("deposits") || this.props.user.showColumn.includes("withdrawals") ? (
																				<>
																					{this.props.user.showColumn.includes("deposits") || this.props.user.level >= 6 ? (
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								<Link
																									onFollow={() => {
																										this.props.Interface.loadPayments(this.props.playerPage.data.id, 1, this.props.playerPage.data.project);
																									}}>
																									<b>Deposits</b>:
																								</Link>
																							</Box>
																							{!this.state.paymentsStats.statsDeposit.loading ? (
																								<Badge
																									className={[this.props.styles["badge"], this.state.paymentsStats.statsDeposit.deposits.total > 0 ? this.props.styles["green"] : this.props.styles["dark"]].join(" ")}
																									children={
																										<>
																											{this.state.paymentsStats.statsDeposit.deposits.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsDeposit.deposits.total) : "0"} RUB. ({this.state.paymentsStats.statsDeposit.deposits.count})
																										</>
																									}
																								/>
																							) : (
																								<Spinner size="big" />
																							)}
																						</SpaceBetween>
																					) : (
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								<b>Deposits</b>
																							</Box>
																							<SpaceBetween size="xl" className={this.props.styles["item"]} direction="horizontal">
																								<Box margin={{ right: "xxl" }} display="inline-block">
																									<Button
																										className={this.props.styles["py0"]}
																										onClick={() => {
																											this.props.Interface.loadPayments(this.props.playerPage.data.id, 1, this.props.playerPage.data.project);
																										}}
																										variant="normal"
																										iconName="search"
																										children="Show"
																									/>
																								</Box>
																							</SpaceBetween>
																						</SpaceBetween>
																					)}

																					{this.props.user.showColumn.includes("withdrawals") || this.props.user.level >= 6 ? (
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								<Link
																									onFollow={() => {
																										this.props.Interface.loadPayments(this.props.playerPage.data.id, 2, this.props.playerPage.data.project);
																									}}>
																									<b>Withdrawals</b>:
																								</Link>
																							</Box>
																							{!this.state.paymentsStats.statsWithdrawals.loading ? (
																								<Badge
																									className={[this.props.styles["badge"], this.state.paymentsStats.statsWithdrawals.withdrawals.total > 0 ? this.props.styles["red"] : this.props.styles["dark"]].join(" ")}
																									children={
																										<>
																											{this.state.paymentsStats.statsWithdrawals.withdrawals.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.paymentsStats.statsWithdrawals.withdrawals.total) : "0"} RUB. ({this.state.paymentsStats.statsWithdrawals.withdrawals.count})
																										</>
																									}
																								/>
																							) : (
																								<Spinner size="big" />
																							)}
																						</SpaceBetween>
																					) : (
																						<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																							<Box variant="span">
																								<b>Withdrawals</b>
																							</Box>
																							<SpaceBetween size="xl" className={this.props.styles["item"]} direction="horizontal">
																								<Box margin={{ right: "xxl" }} display="inline-block">
																									<Button
																										className={this.props.styles["py0"]}
																										onClick={() => {
																											this.props.Interface.loadPayments(this.props.playerPage.data.id, 2, this.props.playerPage.data.project);
																										}}
																										variant="normal"
																										iconName="search"
																										children="Show"
																									/>
																								</Box>
																							</SpaceBetween>
																						</SpaceBetween>
																					)}

																					{((this.props.user.showColumn.includes("deposits") && this.props.user.showColumn.includes("withdrawals")) || this.props.user.level >= 6) && (
																						<>
																							{!this.state.paymentsStats.statsDeposit.loading && !this.state.paymentsStats.statsWithdrawals.loading ? (
																								<SpaceBetween className={[this.props.styles["item"], this.props.styles["profit"], this.state.paymentsStats.statsDeposit.deposits.total - this.state.paymentsStats.statsWithdrawals.withdrawals.total > 0 ? this.props.styles["green"] : this.props.styles["red"]].join(" ")} direction="horizontal">
																									<Box color="inherit" margin={{ top: "xxxs", bottom: "xs" }} variant="div">
																										Profit:
																									</Box>
																									<Badge className={[this.props.styles["badge"], this.state.paymentsStats.statsDeposit.deposits.total - this.state.paymentsStats.statsWithdrawals.withdrawals.total > 0 ? this.props.styles["green"] : this.props.styles["red"]].join(" ")} children={<>{new Intl.NumberFormat("ru-RU").format((this.state.paymentsStats.statsDeposit.deposits.total - this.state.paymentsStats.statsWithdrawals.withdrawals.total).toFixed(2))} RUB.</>} />
																								</SpaceBetween>
																							) : (
																								<Spinner size="big" />
																							)}
																						</>
																					)}
																				</>
																			) : (
																				<>
																					<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																						<Box variant="span">
																							<b>Deposits</b>
																						</Box>
																						<SpaceBetween size="xl" className={this.props.styles["item"]} direction="horizontal">
																							<Box margin={{ right: "xxl" }} display="inline-block">
																								<Button
																									className={this.props.styles["py0"]}
																									onClick={() => {
																										this.props.Interface.loadPayments(this.props.playerPage.data.id, 1, this.props.playerPage.data.project);
																									}}
																									variant="normal"
																									iconName="search"
																									children="Show"
																								/>
																							</Box>
																						</SpaceBetween>
																					</SpaceBetween>

																					<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																						<Box variant="span">
																							<b>Withdrawals</b>
																						</Box>
																						<SpaceBetween size="xl" className={this.props.styles["item"]} direction="horizontal">
																							<Box margin={{ right: "xxl" }} display="inline-block">
																								<Button
																									className={this.props.styles["py0"]}
																									onClick={() => {
																										this.props.Interface.loadPayments(this.props.playerPage.data.id, 2, this.props.playerPage.data.project);
																									}}
																									variant="normal"
																									iconName="search"
																									children="Show"
																								/>
																							</Box>
																						</SpaceBetween>
																					</SpaceBetween>
																				</>
																			)}

																			<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																				<Box variant="span">
																					<b>Promo code</b>
																				</Box>
																				{this.state.promocode.loading ? <Spinner size="big" /> : <Badge className={[this.props.styles["badge"], this.state.promocode.total > 0 ? this.props.styles["cyan"] : this.props.styles["dark"]].join(" ")} children={<>{this.state.promocode.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.promocode.total) : "0"} RUB.</>} />}
																			</SpaceBetween>
																			<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																				<Box variant="span">
																					<b>Free wheel</b>
																				</Box>
																				{this.state.wheel.loading ? <Spinner size="big" /> : <Badge className={[this.props.styles["badge"], this.state.wheel.total > 0 ? this.props.styles["cyan"] : this.props.styles["dark"]].join(" ")} children={<>{this.state.wheel.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.wheel.total) : "0"} RUB.</>} />}
																			</SpaceBetween>
																			<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																				<Box variant="span">
																					<b>Boost</b>
																				</Box>
																				{this.state.boost.loading ? <Spinner size="big" /> : <Badge className={[this.props.styles["badge"], this.state.boost.total > 0 ? this.props.styles["orange"] : this.props.styles["dark"]].join(" ")} children={<>{this.state.boost.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.boost.total) : "0"} RUB.</>} />}
																			</SpaceBetween>
																			<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																				<Box variant="span">
																					<b>VK Bonus</b>
																				</Box>
																				{this.state.vkBonus.loading ? <Spinner size="big" /> : <Badge className={[this.props.styles["badge"], this.state.vkBonus.total > 0 ? this.props.styles["vk"] : this.props.styles["dark"]].join(" ")} children={<>{this.state.vkBonus.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.vkBonus.total) : "0"} RUB.</>} />}
																			</SpaceBetween>
																			<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																				<Box variant="span">
																					<b>Free Cash</b>
																				</Box>
																				<Badge className={[this.props.styles["badge"], this.props.playerPage.data.economic.freecash > 0 ? this.props.styles["gold"] : this.props.styles["dark"]].join(" ")} children={<>{this.props.playerPage.data.economic.freecash > 0 ? new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.economic.freecash) : "0"} RUB.</>} />
																			</SpaceBetween>
																			<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																				<Box variant="span">
																					<b>Rakeback programm</b>
																				</Box>
																				{!this.state.rakeback.loading ? <Badge className={[this.props.styles["badge"], this.state.rakeback.total > 0 ? this.props.styles["lightOrange"] : this.props.styles["dark"]].join(" ")} children={<>{this.state.rakeback.total > 0 ? new Intl.NumberFormat("ru-RU").format(this.state.rakeback.total) : "0"} RUB.</>} /> : <Spinner size="big" />}
																			</SpaceBetween>
																			{typeof this.props.playerPage.data.economic.aff !== "undefined" ? (
																				<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																					<Box variant="span">
																						<b>Partner programm</b>
																					</Box>
																					<Badge className={[this.props.styles["badge"], this.props.playerPage.data.economic.aff > 0 ? this.props.styles["purple"] : this.props.styles["dark"]].join(" ")} children={<>{this.props.playerPage.data.economic.aff > 0 ? new Intl.NumberFormat("ru-RU").format(this.props.playerPage.data.economic.aff) : "0"} RUB.</>} />
																				</SpaceBetween>
																			) : (
																				<></>
																			)}

																			<SpaceBetween className={[this.props.styles["item"], this.props.styles["noHr"]].join(" ")} direction="horizontal">
																				<Box variant="span">
																					{!this.props.playerPage.data.project || this.props.user.brand.id === this.props.playerPage.data.project ? (
																						<Link
																							onFollow={() => {
																								this.props.setPlayerPage({ ...this.props.playerPage, show: false, loaded: false, log: { ...this.props.playerPage.log, loaded: false } });
																								window.location.href = "/players?refer=" + this.props.playerPage.data.id;
																							}}>
																							<b>Referrals</b>
																						</Link>
																					) : (
																						<b>Referrals</b>
																					)}
																				</Box>
																				{!this.state.totalReferer.loading ? <Badge className={[this.props.styles["badge"], this.state.totalReferer.total > 0 ? this.props.styles["magenta"] : this.props.styles["dark"]].join(" ")} children={<>{this.state.totalReferer.total > 0 ? this.state.totalReferer.total : "0"} referrals</>} /> : <Spinner size="big" />}
																			</SpaceBetween>
																		</SpaceBetween>
																	</Container>
																		{this.renderWelcomeBonusSection()}
																		{(this.props.user.showColumn.includes("showPartnerSettings") || this.props.user.level >= 6) && (
																		<Box margin={{ top: "m" }}>
																			<PlayerProfilePartnerSettingsSection Interface={this.props.Interface} uid={this.props.playerPage?.uid} />
																		</Box>
																	)}
																	{(this.props.user.showColumn.includes("postbacks") || this.props.user.level >= 6) && (
																		<Box margin={{ top: "m" }}>
																			<PlayerProfilePostbacksSection Interface={this.props.Interface} uid={this.props.playerPage?.uid} />
																		</Box>
																	)}
																	{(this.props.user.showColumn.includes("showDepositLadder") || this.props.user.level >= 6) && (
																		<Box margin={{ top: "m" }}>
																			<PlayerProfileDepositLadderSection Interface={this.props.Interface} uid={this.props.playerPage?.uid} />
																		</Box>
																	)}
																	{(this.props.user.showColumn.includes("showPartnerStats") || this.props.user.level >= 6) && <PlayerProfilePartnerStatsSection Interface={this.props.Interface} uid={this.props.playerPage?.uid} project={this.props.playerPage?.data?.project} user={this.props.user} />}
																	<Box margin={{ top: "m", bottom: "m" }} style={{ position: "relative" }}>
																		{(this.props.user.showColumn.includes("showPrivateComment") || this.props.user.level >= 6) && <Textarea rows="3" placeholder="Leave comment here" value={this.state.privateComment} onChange={(a) => this.addComment(a.detail.value, "private")} />}

																		<Box margin={{ top: "m", bottom: "m" }} style={{ position: "relative" }}>
																			<Textarea rows="3" placeholder="Leave support comment here" value={this.state.publicComment} onChange={(a) => this.addComment(a.detail.value, "public")} />
																		</Box>
																	</Box>
																	{(this.props.user.showColumn.includes("gamesSessions") || this.props.user.level >= 6) && (
																		<Box
																			margin={{ top: "m", bottom: "s" }}
																			children={
																				<Table
																					loading={this.props.playerPage.data.casinoID ? this.state.logCasino.loading : false}
																					empty="No player activity history"
																					pagination={
																						<Pagination
																							currentPageIndex={this.props.playerPage.gameLog.pagination.current}
																							pagesCount={this.props.playerPage.gameLog.pagination.total}
																							onChange={(page) => {
																								this.props.setPlayerPage({ ...this.props.playerPage, gameLog: { ...this.props.playerPage.gameLog, pagination: { ...this.props.playerPage.gameLog.pagination, current: page.detail.currentPageIndex } } });
																							}}
																						/>
																					}
																					filter={
																						<Input
																							disableBrowserAutocorrect={true}
																							type="search"
																							inputMode="search"
																							placeholder="Game name, game ID"
																							value={this.props.playerPage.gameLog.currentSearch}
																							onChange={(input) => {
																								this.props.setPlayerPage({ ...this.props.playerPage, gameLog: { ...this.props.playerPage.gameLog, currentSearch: input.detail.value } });
																								this.debounceGameLogFilter();
																							}}
																						/>
																					}
																					columnDefinitions={[
																						{
																							maxWidth: 290,
																							id: "game",
																							header: "Game",
																							cell: (item) => (
																								<Link
																									onFollow={() => {
																										this.props.Interface.loadSessionsLog(this.props.playerPage.data.casinoID, item.gameID, project, this.props.playerPage.uid, sessionsPageSize);
																									}}>
																									{item.gameSearch}
																								</Link>
																							),
																						},
																						{ id: "operations", header: "Operations", cell: (item) => item.operations },
																						{ id: "date", header: "Last activity", cell: (item) => new Date(item.last).toLocaleDateString() + " " + new Date(item.last).toLocaleTimeString() },
																					]}
																					items={this.state.logCasino.data
																						.filter((item) => {
																							return item.filtered;
																						})
																						.slice((this.props.playerPage.gameLog.pagination.current - 1) * this.props.config.players.gameLogShort.perPage, this.props.playerPage.gameLog.pagination.current * this.props.config.players.gameLogShort.perPage)}
																				/>
																			}
																		/>
																	)}
																	{(String(this.props.playerPage.data.social_accounts.vk) !== "0" || String(this.props.playerPage.data.social_accounts.fb) !== "0" || String(this.props.playerPage.data.social_accounts.gl) !== "0" || String(this.props.playerPage.data.social_accounts.ok) !== "0" || String(this.props.playerPage.data.social_accounts.st) !== "0" || String(this.props.playerPage.data.social_accounts.tg) !== "0") && (
																		<Container>
																			<SpaceBetween className={this.props.styles["socialItems"]} size="xl" direction="vertical">
																				{this.renderSocialAccount("vk", "VK", "https://vk.com/id", "vk")}
																				{this.renderSocialAccount("fb", "Facebook", "https://www.facebook.com/profile.php?id=", "fb")}
																				{this.renderSocialAccount("gl", "Google", "#", "gl")}
																				{this.renderSocialAccount("ok", "OK", "https://ok.ru/profile/", "ok")}
																				{this.renderSocialAccount("st", "Steam", "https://steamcommunity.com/profiles/", "st")}
																				{this.renderSocialAccount("tg", "Telegram", "#", "tg")}
																			</SpaceBetween>
																		</Container>
																	)}{" "}
																	{(this.props.user.showColumn.includes("antiFrod") || this.props.user.level >= 6) && (
																		<>
																			{(this.props.user.showColumn.includes("antiFrod") || this.props.user.level >= 6) && (
																				<Container className={this.props.styles["antiFrodForm"]}>
																					<Header variant="h3">Anti-Fraud Information</Header>
																					<SpaceBetween size="l" direction="vertical">
																						<SpaceBetween className={this.props.styles["socialItems"]} size="l" direction="vertical">
																							<SpaceBetween className={this.props.styles["item"]} direction="horizontal">
																								<Box variant="span" style={{ fontWeight: "bold", fontSize: "18px" }}>
																									<b>Country</b>:
																								</Box>
																								<Badge className={[this.props.styles["badge"], incorrectCountry.success ? this.props.styles["red"] : this.props.styles["green"]].join(" ")} children={<>{incorrectCountry.success ? `${incorrectCountry.country}` : `${incorrectCountry.country}`}</>} />
																							</SpaceBetween>
																						</SpaceBetween>

																						<Tabs
																							onChange={({ detail }) => {
																								if (detail.activeTabId === "first") {
																									this.props.Interface.loadMultiWithdrawalInfo(this.props.playerPage.uid, "withdrawals", project, { page: this.props.multiWithdrawal.multiWithdrawals.pagination.current, limit: multiWithdrawalsPageSize });
																								} else {
																									this.props.Interface.loadMultiWithdrawalInfo(this.props.playerPage.uid, "multiPlayers", project, { page: this.props.multiWithdrawal.multiPlayers.pagination.current, limit: multiPlayersPageSize });
																								}
																							}}
																							tabs={[
																								{
																									label: "Multi withdrawals",
																									id: "first",
																									content: (
																										<>
																											<Table
																												className={this.props.styles["withdrawalsMulti"]}
																												loading={!this.props.multiWithdrawal.loaded}
																												header={
																													<Header variant="h3" counter={`(${this.props.multiWithdrawal?.multiWithdrawals?.pagination?.total || 0})`}>
																														Multi withdrawals
																													</Header>
																												}
																												columnDefinitions={[
																													{ id: "uid", header: "UID", cell: (item) => item.uid },
																													{ id: "project", header: "Project", cell: (item) => item.project },
																													{ id: "wallet", header: "Wallet", cell: (item) => item.wallet },
																													{ id: "verify", header: "Verify", cell: (item) => (item.userInfo.verify ? (item.userInfo?.verify?.status === 2 ? "Yes" : item.userInfo?.verify?.status === 3 ? "Rejected" : item.userInfo?.verify?.status === 1 ? "Waiting" : "No") : "No") },
																													{ id: "referer", header: "Referer", cell: (item) => (item.userInfo.referer ? item.userInfo.referer : "No") },
																													{ id: "date", header: "Date", cell: (item) => new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString() },
																												]}
																												items={this.props.multiWithdrawal?.multiAccount?.withdrawals}
																												pagination={
																													<Pagination
																														currentPageIndex={this.props.multiWithdrawal.multiWithdrawals.pagination.current}
																														pagesCount={this.props.multiWithdrawal.multiWithdrawals.pagination.totalPage}
																														onChange={(page) => {
																															this.props.setMultiWithdrawal({ ...this.props.multiWithdrawal, multiWithdrawals: { ...this.props.multiWithdrawal.multiWithdrawals, pagination: { ...this.props.multiWithdrawal.multiWithdrawals.pagination, current: page.detail.currentPageIndex } } });

																															setTimeout(() => {
																																this.props.Interface.loadMultiWithdrawalInfo(this.props.playerPage.uid, "withdrawals", project, { page: page.detail.currentPageIndex, limit: multiWithdrawalsPageSize });
																															}, 250);
																														}}
																													/>
																												}
																												preferences={
																													<Preferences
																														cancelLabel="Cancel"
																														confirmLabel="Confirm"
																														pageSizePreference={{ title: "Logs per page", options: this.props.config.players.multiWithdrawals.preferences.perPage }}
																														preferences={{ pageSize: multiWithdrawalsPageSize }}
																														title="Preferences"
																														onConfirm={(preferences) => {
																															const pageSize = preferences.detail.pageSize;
																															const perPageByProject = this.saveProjectPageSize(this.props.config.players.multiWithdrawals, project, pageSize, "players.multiWithdrawals.perPageByProject");
																															this.props.setConfig({ ...this.props.config, players: { ...this.props.config.players, multiWithdrawals: { ...this.props.config.players.multiWithdrawals, perPageByProject } } });
																															this.props.setMultiWithdrawal({ ...this.props.multiWithdrawal, multiWithdrawals: { ...this.props.multiWithdrawal.multiWithdrawals, pagination: { ...this.props.multiWithdrawal.multiWithdrawals.pagination, current: 1 } } });
																															this.props.Interface.loadMultiWithdrawalInfo(this.props.playerPage.uid, "withdrawals", project, { page: 1, limit: pageSize });
																														}}
																													/>
																												}
																												empty="No multi player wallet"
																											/>
																										</>
																									),
																								},
																								{
																									label: "Multi players",
																									id: "second",
																									content: (
																										<>
																											<Table
																												className={this.props.styles["withdrawalsMulti"]}
																												loading={!this.props.multiWithdrawal.loaded}
																												header={
																													<Header variant="h3" counter={`(${this.props.multiWithdrawal?.multiPlayers?.pagination?.total || 0})`}>
																														Multi players
																													</Header>
																												}
																												columnDefinitions={[
																													{ id: "uid", header: "UID", cell: (item) => item.uid },
																													{ id: "project", header: "Project", cell: (item) => item.project },
																													{ id: "verify", header: "Verify", cell: (item) => (item.userInfo.verify ? (item.userInfo?.verify?.status === 2 ? "Yes" : item.userInfo?.verify?.status === 3 ? "Rejected" : item.userInfo?.verify?.status === 1 ? "Waiting" : "No") : "No") },
																													{ id: "referer", header: "Referer", cell: (item) => (item.userInfo.referer ? item.userInfo.referer : "No") },
																													{ id: "ip", header: "Ip", cell: (item) => item.ip },
																													{ id: "browser", header: "Browser", cell: (item) => item.browser },
																													{ id: "country", header: "Country", cell: (item) => item.country },
																													{ id: "lang", header: "Language", cell: (item) => item.lang },
																													{ id: "os", header: "Os", cell: (item) => item.os },
																													{ id: "region", header: "Region", cell: (item) => item.region },
																													{ id: "model", header: "Model", cell: (item) => item.model },
																													{ id: "brand", header: "Brand", cell: (item) => item.brand },
																													{ id: "screen", header: "Screen", cell: (item) => item.screen },
																													{ id: "type", header: "Type", cell: (item) => item.type },
																													{ id: "userAgent", header: "UserAgent", cell: (item) => item.userAgent },
																													{ id: "date", header: "Date", cell: (item) => new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString() },
																												]}
																												items={this.props.multiWithdrawal.multiAccount?.multiPlayers}
																												pagination={
																													<Pagination
																														currentPageIndex={this.props.multiWithdrawal.multiPlayers.pagination.current}
																														pagesCount={this.props.multiWithdrawal.multiPlayers.pagination.totalPage}
																														onChange={(page) => {
																															this.props.setMultiWithdrawal({ ...this.props.multiWithdrawal, multiPlayers: { ...this.props.multiWithdrawal.multiPlayers, pagination: { ...this.props.multiWithdrawal.multiPlayers.pagination, current: page.detail.currentPageIndex } } });

																															setTimeout(() => {
																																this.props.Interface.loadMultiWithdrawalInfo(this.props.playerPage.uid, "multiPlayers", project, { page: page.detail.currentPageIndex, limit: multiPlayersPageSize });
																															}, 250);
																														}}
																													/>
																												}
																												preferences={
																													<Preferences
																														cancelLabel="Cancel"
																														confirmLabel="Confirm"
																														pageSizePreference={{ title: "Logs per page", options: this.props.config.players.multiPlayers.preferences.perPage }}
																														preferences={{ pageSize: multiPlayersPageSize }}
																														title="Preferences"
																														onConfirm={(preferences) => {
																															const pageSize = preferences.detail.pageSize;
																															const perPageByProject = this.saveProjectPageSize(this.props.config.players.multiPlayers, project, pageSize, "players.multiPlayers.perPageByProject");
																															this.props.setConfig({ ...this.props.config, players: { ...this.props.config.players, multiPlayers: { ...this.props.config.players.multiPlayers, perPageByProject } } });
																															this.props.setMultiWithdrawal({ ...this.props.multiWithdrawal, multiPlayers: { ...this.props.multiWithdrawal.multiPlayers, pagination: { ...this.props.multiWithdrawal.multiPlayers.pagination, current: 1 } } });
																															this.props.Interface.loadMultiWithdrawalInfo(this.props.playerPage.uid, "multiPlayers", project, { page: 1, limit: pageSize });
																														}}
																													/>
																												}
																												empty="No multi player"
																											/>
																										</>
																									),
																								},
																							]}
																						/>
																					</SpaceBetween>
																				</Container>
																			)}
																		</>
																	)}
																</>
															}
														/>
													</>
												}
											/>
										}
									/>
									<div ref={this.scrollRef}></div>
									<Tabs
										onChange={(tab) => {
											this.switchTab(tab.detail.activeTabId);

											if (tab.detail.activeTabId === "sweepstakes") {
												if (this.props.playerPage.bonuses.sweepstakesActive.data.length === 0) {
													setTimeout(() => {
														this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
													}, 250);
												}
											}

											if (tab.detail.activeTabId === "achievements") {
												if (this.props.playerPage.bonuses.achievementsActive.data.length === 0) {
													setTimeout(() => {
														this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
													}, 250);
												}
											}
										}}
										tabs={[
											{
												id: "all20000",
												label: "Logs (20000)",
												content: (
													<Table
														stickyHeader="true"
														loading={!this.props.playerPage.log.loaded}
														columnDefinitions={[
															{
																id: "operation",
																header: "Operation",
																width: 440,
																cell: (item) => (
																	<>
																		<div style={{ display: "flex" }}>
																			{(this.props.user.level >= 6 || this.props.user.showColumn.includes("resetLogButton")) && item.logId !== undefined && (
																				<input
																					value={item.logId}
																					onChange={(e) => {
																						this.setState({ ...this.state, resetId: e.target.value });
																					}}
																					style={{ marginBottom: 6 }}
																					type="radio"
																					name="fav_language"
																				/>
																			)}
																		</div>
																		{typeof item.expanded !== "undefined" && Number(item.type) !== 181 ? (
																			<>
																				<ExpandableSection
																					id={item.id}
																					expanded={item.expanded}
																					onChange={(expand) => {
																						this.switchLogExpand(item, expand.detail.expanded);
																					}}
																					header={<Badge className={[this.props.styles["badge"], this.props.styles["tab"], this.props.styles[this.getTypeColor(item)]].join(" ")} children={this.getTypeByID(item).toUpperCase()} />}
																					children={item.isLoading ? <span>Operation loading</span> : <span>Inner operations: {item.operations}</span>}
																				/>
																			</>
																		) : (
																			<Box margin={{ left: "xl" }}>
																				<Badge className={[this.props.styles["badge"], this.props.styles["tab"], this.props.styles[this.getTypeColor(item)]].join(" ")} children={this.getTypeByID(item).toUpperCase()} />
																			</Box>
																		)}{" "}
																	</>
																),
															},
															{ id: "date", header: "Date", cell: (item) => new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString() },
															{
																id: "amount",
																header: "Amount",
																cell: (item) => (
																	<Box variant="span" color={item.amount >= 0 ? (item.amount > 0 ? "text-status-success" : "text-body-secondary") : "text-status-error"}>
																		{new Intl.NumberFormat("ru-RU").format(item.amount)} RUB.{" "}
																		{typeof item.wager !== "undefined" ? (
																			<>
																				({new Intl.NumberFormat("ru-RU").format(item.wager)} RUB.) <span className={[this.props.styles["text"], this.props.styles["purple"]].join(" ")}>({new Intl.NumberFormat("ru-RU").format(item.amountBonus ? item.amountBonus : 0)} RUB.)</span>{" "}
																			</>
																		) : (
																			<></>
																		)}
																	</Box>
																),
															},
															{ id: "from", header: "From", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.from / 100)} RUB.</> },
															{ id: "to", header: "To", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.to / 100)} RUB.</> },
															{ id: "operations", header: "Operations", cell: (item) => item.operations },
															{ id: "firstOperation", header: "First operation", cell: (item) => <Icon name={typeof item.expanded === "undefined" ? "" : !item.firstOperation ? "status-stopped" : "status-positive"} variant={typeof item.expanded === "undefined" ? "info" : item.firstOperation ? "success" : "error"} /> },
														]}
														items={
															this.state.filteredData.length > 0 ? this.state.filteredData.slice((this.props.playerPage.pagination.current - 1) * this.props.config.players.log.perPage, this.props.playerPage.pagination.current * this.props.config.players.log.perPage) : this.props.playerPage.log.data.slice((this.props.playerPage.pagination.current - 1) * this.props.config.players.log.perPage, this.props.playerPage.pagination.current * this.props.config.players.log.perPage)
														}
														pagination={
															<Pagination
																currentPageIndex={this.props.playerPage.pagination.current}
																pagesCount={this.props.playerPage.pagination.total}
																onChange={(page) => {
																	this.props.setPlayerPage({ ...this.props.playerPage, pagination: { ...this.props.playerPage.pagination, current: page.detail.currentPageIndex } });
																}}
															/>
														}
														preferences={
															<Preferences
																cancelLabel="Cancel"
																confirmLabel="Confirm"
																pageSizePreference={{ title: "Logs per page", options: this.props.config.players.log.preferences.perPage }}
																preferences={{ pageSize: this.props.config.players.log.perPage }}
																title="Preferences"
																onConfirm={(preferences) => {
																	localStorage.setItem("players.log.perPage", preferences.detail.pageSize);
																	this.props.setConfig({ ...this.props.config, players: { ...this.props.config.players, log: { ...this.props.config.players.log, perPage: preferences.detail.pageSize } } });

																	this.scheduleTimeout(() => {
																		this.props.setPlayerPage({
																			...this.props.playerPage,
																			pagination: {
																				current: 1,
																				total: Math.ceil(
																					this.props.playerPage.log.data.filter((item) => {
																						return typeof item.mainItem === "undefined" || this.isItemOpen(item.mainItem);
																					}).length / preferences.detail.pageSize,
																				),
																			},
																		});
																	}, 0);
																}}
															/>
														}
														filter={
															<>
																<Input
																	disableBrowserAutocorrect={true}
																	placeholder="Operation type, amount or something"
																	value={this.props.playerPage.log.currentSearch}
																	onChange={(input) => {
																		this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, currentSearch: input.detail.value } });
																		this.debounceFilter(250);
																	}}
																/>
																{(this.props.user.level >= 6 || this.props.user.showColumn.includes("resetLogButton")) && (
																	<>
																		<Box margin={{ top: "s" }}>
																			<div style={{ display: "flex", marginTop: 10 }}>
																				<Button
																					disabled={!this.props.playerPage.log.loaded || this.state.resetId === 0}
																					onClick={() => {
																						this.props.Interface.resetLog(this.props.playerPage.uid, this.state.resetId, 0).then(() => {
																							this.setState({ ...this.state, resetId: 0 });
																							this.props.Interface.loadLog(this.props.playerPage.uid, this.props.playerPage.data.project);
																						});
																					}}>
																					Reset log
																				</Button>

																				<div style={{ marginLeft: 5 }}>
																					<Button
																						disabled={!this.props.playerPage.log.loaded || this.state.resetId === 0}
																						onClick={() => {
																							this.props.Interface.resetLog(this.props.playerPage.uid, this.state.resetId, 1).then(() => {
																								this.setState({ ...this.state, resetId: 0 });
																								this.props.Interface.loadLog(this.props.playerPage.uid, this.props.playerPage.data.project);
																							});
																						}}>
																						Reset log all
																					</Button>
																				</div>
																			</div>
																		</Box>
																	</>
																)}
															</>
														}
														empty="No logs found with requested filter"
													/>
												),
											},
											...(this.props.user.level >= 6 || this.props.user.showColumn.includes("loginHistory")
												? [
														{
															id: "login",
															label: "Login history",
															content: (
																<Table
																	loading={!this.props.playerPage.log.loaded}
																	sortingColumn={{ sortingField: this.props.playerPage.sorting.sortingColumn.sortingField }}
																	sortingDescending={this.props.playerPage.sorting.isDescending}
																	onSortingChange={(sorting) => {
																		this.props.setPlayerPage({ ...this.props.playerPage, sorting: sorting.detail });
																		this.scheduleTimeout(() => {
																			this.props.setPlayerPage({
																				...this.props.playerPage,
																				data: {
																					...this.props.playerPage.data,
																					regdata: {
																						...this.props.playerPage.data.regdata,
																						last: cloneDeep(this.props.playerPage.data.regdata.last).sort((a, b) => {
																							return this.compare(a, b);
																						}),
																					},
																				},
																			});
																		}, 0);
																	}}
																	columnDefinitions={[
																		{
																			id: "country",
																			header: "Country",
																			sortingField: "country",
																			cell: (item) => item.country,
																		},
																		{
																			id: "lang",
																			header: "Language",
																			sortingField: "lang",
																			cell: (item) => item.lang,
																		},
																		{
																			id: "region",
																			header: "Region",
																			sortingField: "region",
																			cell: (item) => item.region,
																		},
																		{
																			id: "browser",
																			header: "Browser",
																			sortingField: "browser",
																			cell: (item) => item.browser,
																		},
																		{ id: "os", header: "OS", cell: (item) => item.os },
																		{ id: "ip", header: "IP", cell: (item) => item.ip },
																		{ id: "timezone", header: "Timezone", cell: (item) => item.timezone || "—" },
																		{
																			id: "date",
																			header: "Date",
																			sortingField: "date",
																			cell: (item) => <>{new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString()}</>,
																		},
																	]}
																	items={this.props.playerPage.data.regdata.last.slice((this.props.playerPage.pagination.current - 1) * this.props.config.players.log.perPage, this.props.playerPage.pagination.current * this.props.config.players.log.perPage)}
																	pagination={
																		<Pagination
																			currentPageIndex={this.props.playerPage.pagination.current}
																			pagesCount={this.props.playerPage.pagination.total}
																			onChange={(page) => {
																				this.props.setPlayerPage({
																					...this.props.playerPage,
																					pagination: {
																						...this.props.playerPage.pagination,
																						current: page.detail.currentPageIndex,
																					},
																				});
																			}}
																		/>
																	}
																	preferences={
																		<Preferences
																			cancelLabel="Cancel"
																			confirmLabel="Confirm"
																			pageSizePreference={{
																				title: "Logs per page",
																				options: this.props.config.players.log.preferences.perPage,
																			}}
																			preferences={{ pageSize: this.props.config.players.log.perPage }}
																			title="Preferences"
																			onConfirm={(preferences) => {
																				localStorage.setItem("players.log.perPage", preferences.detail.pageSize);
																				this.props.setConfig({
																					...this.props.config,
																					players: {
																						...this.props.config.players,
																						log: { ...this.props.config.players.log, perPage: preferences.detail.pageSize },
																					},
																				});
																				this.props.setPlayerPage({
																					...this.props.playerPage,
																					pagination: {
																						current: 1,
																						total: Math.ceil(this.props.playerPage.data.regdata.last.length / preferences.detail.pageSize),
																					},
																				});
																			}}
																		/>
																	}
																	filter={
																		<Input
																			disableBrowserAutocorrect={true}
																			placeholder="IP, country or something"
																			value={this.props.playerPage.log.currentSearch}
																			onChange={(input) => {
																				this.props.setPlayerPage({
																					...this.props.playerPage,
																					log: { ...this.props.playerPage.log, currentSearch: input.detail.value },
																				});
																				this.debounceFilter();
																			}}
																		/>
																	}
																	empty="No login history found with requested filter"
																/>
															),
														},
												  ]
												: []),
											{
												id: "actionLog",
												label: `Action log ${this.props.userActionLog.pagination.count === 0 ? "" : "(" + this.props.userActionLog.pagination.count + ")"}`,
												content: (
													<Table
														stickyHeader="true"
														loading={!this.props.userActionLog.loaded}
														sortingColumn={{ sortingField: this.props.userActionLog.sorting.sortingColumn.column }}
														sortingDescending={this.props.userActionLog.sorting.isDescending}
														onSortingChange={(sorting) => {
															this.props.setUserActionLog({
																...this.props.userActionLog,
																sorting: {
																	...this.props.userActionLog.sorting,
																	sortingColumn: {
																		order: !sorting.detail.isDescending ? "ASC" : "DESC",
																		column: sorting.detail.sortingColumn.sortingField,
																	},
																	isDescending: sorting.detail.isDescending,
																},
															});

															setTimeout(() => {
																this.props.Interface.loadUserActionLog(this.props.playerPage.uid);
															}, 250);
														}}
														columnDefinitions={[
															{
																id: "createdAt",
																header: "Date",
																sortingField: "createdAt",
																cell: (item) => new Date(item.createdAt).toLocaleDateString() + " " + new Date(item.createdAt).toLocaleTimeString(),
															},
															{
																id: "action",
																header: "Action",
																sortingField: "action",
																cell: (item) => item.action,
															},
															{
																id: "details",
																header: "Details",
																sortingField: false,
																cell: (item) => this.renderActionLogDetails(item),
															},
														]}
														items={this.props.userActionLog.data}
														pagination={
															<Pagination
																disabled={!this.props.userActionLog.loaded}
																currentPageIndex={this.props.userActionLog.pagination.current}
																pagesCount={this.props.userActionLog.pagination.total}
																onChange={(page) => {
																	this.props.setUserActionLog({
																		...this.props.userActionLog,
																		pagination: {
																			...this.props.userActionLog.pagination,
																			current: page.detail.currentPageIndex,
																		},
																	});
																	setTimeout(() => this.props.Interface.loadUserActionLog(this.props.playerPage.uid), 250);
																}}
															/>
														}
														preferences={
															<Preferences
																cancelLabel="Cancel"
																confirmLabel="Confirm"
																pageSizePreference={{ title: "Action log per page", options: this.props.config.userActionLog.preferences.perPage }}
																preferences={{ pageSize: this.props.config.userActionLog.perPage }}
																title="Preferences"
																onConfirm={(preferences) => {
																	localStorage.setItem("players.userActionLog.perPage", preferences.detail.pageSize);
																	this.props.setConfig({ ...this.props.config, userActionLog: { ...this.props.config.userActionLog, perPage: preferences.detail.pageSize } });

																	setTimeout(() => {
																		this.props.Interface.loadUserActionLog(this.props.playerPage.uid);
																	}, 250);
																}}
															/>
														}
														filter={
															<Box margin={{ bottom: "m" }}>
																<Select
																	filteringType="auto"
																	disabled={!this.props.userActionLog.loaded}
																	placeholder="Action"
																	selectedOption={this.props.userActionLog.currentOption.action}
																	onChange={(option) => {
																		this.props.setUserActionLog({
																			...this.props.userActionLog,
																			currentSearchType: { value: "action" },
																			currentSearch: option.detail.selectedOption.value,
																			currentOption: { ...this.props.userActionLog.currentOption, action: option.detail.selectedOption },
																			pagination: {
																				...this.props.userActionLog.pagination,
																				current: 1,
																			},
																		});

																		setTimeout(() => {
																			this.props.Interface.loadUserActionLog(this.props.playerPage.uid);
																		}, 250);
																	}}
																	options={[
																		{ label: "All actions", value: "" },
																		{ label: "currencyChange", value: "currencyChange" },
																	]}
																/>
															</Box>
														}
														header={<Box margin={{ bottom: "m" }} children={<Header children="Action log" counter={<>({this.props.userActionLog.pagination.count})</>} />} />}
														empty="No action log found"
													/>
												),
											},

											{
												id: "promocodes",
												label: `Promocode ${this.props.playerPage.bonuses.allPromocode.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.allPromocode.pagination.count + ")"}`,
												content: (
													<Table
														loading={!this.props.playerPage.bonuses.allPromocode.loaded}
														sortingColumn={{ sortingField: this.props.playerPage.bonuses.allPromocode.sorting.sortingColumn.column }}
														sortingDescending={this.props.playerPage.bonuses.allPromocode.sorting.isDescending}
														onSortingChange={(sorting) => {
															this.props.setPlayerPage({
																...this.props.playerPage,
																bonuses: {
																	...this.props.playerPage.bonuses,
																	allPromocode: {
																		...this.props.playerPage.bonuses.allPromocode,
																		sorting: {
																			...this.props.playerPage.bonuses.allPromocode.sorting,
																			sortingColumn: {
																				order: !sorting.detail.isDescending ? "ASC" : "DESC",
																				column: sorting.detail.sortingColumn.sortingField,
																			},
																			isDescending: sorting.detail.isDescending,
																		},
																	},
																},
															});

															setTimeout(() => {
																this.props.Interface.loadAllUserPromo(this.props.playerPage.uid, this.props.playerPage.data.project);
															}, 250);
														}}
														columnDefinitions={[
															{ id: "code", header: "Code", sortingField: "code", cell: (item) => item.code },
															{
																id: "value",
																header: "Value",
																sortingField: "value",
																cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.value)} RUB.</>,
															},
															{ id: "count", header: "Count", sortingField: "count", cell: (item) => item.count },
															{ id: "note", header: "Note", cell: (item) => item.note },
															{ id: "date", header: "Date", sortingField: "date", cell: (item) => <>{new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString()}</> },
														]}
														items={this.props.playerPage.bonuses.allPromocode.data}
														pagination={
															<Pagination
																currentPageIndex={this.props.playerPage.bonuses.allPromocode.pagination.current}
																pagesCount={this.props.playerPage.bonuses.allPromocode.pagination.total}
																onChange={(page) => {
																	this.props.setPlayerPage({
																		...this.props.playerPage,
																		bonuses: {
																			...this.props.playerPage.bonuses,
																			allPromocode: {
																				...this.props.playerPage.bonuses.allPromocode,
																				pagination: {
																					...this.props.playerPage.bonuses.allPromocode.pagination,
																					current: page.detail.currentPageIndex,
																				},
																			},
																		},
																	});

																	setTimeout(() => {
																		this.props.Interface.loadAllUserPromo(this.props.playerPage.uid, this.props.playerPage.data.project);
																	}, 250);
																}}
															/>
														}
														preferences={
															<Preferences
																cancelLabel="Cancel"
																confirmLabel="Confirm"
																pageSizePreference={{ title: "Bonuses promocode per page", options: this.props.config.players.bonuses.promocode.preferences.perPage }}
																preferences={{ pageSize: this.props.config.players.bonuses.promocode.perPage }}
																title="Preferences"
																onConfirm={(preferences) => {
																	localStorage.setItem("players.bonuses.promocode.perPage", preferences.detail.pageSize);
																	this.props.setConfig({
																		...this.props.config,
																		players: {
																			...this.props.config.players,
																			bonuses: {
																				...this.props.config.players.bonuses,
																				promocode: {
																					...this.props.config.players.bonuses.promocode,
																					perPage: preferences.detail.pageSize,
																				},
																			},
																		},
																	});
																	this.props.setPlayerPage({ ...this.props.playerPage, bonuses: { ...this.props.playerPage.bonuses, allPromocode: { ...this.props.playerPage.bonuses.allPromocode, pagination: { ...this.props.playerPage.bonuses.allPromocode.pagination, current: 1 } } } });

																	setTimeout(() => {
																		this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project);
																	}, 250);
																}}
															/>
														}
														filter={
															<Grid
																gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}
																children={
																	<>
																		<Select
																			filteringType="auto"
																			disabled={!this.props.playerPage.bonuses.allPromocode.loaded}
																			placeholder="Type"
																			selectedOption={this.props.playerPage.bonuses.allPromocode.currentOption.type}
																			onChange={(option) => {
																				option.detail.selectedOption.type !== this.props.playerPage.bonuses.allPromocode.currentSearchType &&
																					this.props.setPlayerPage({
																						...this.props.playerPage,
																						bonuses: {
																							...this.props.playerPage.bonuses,
																							allPromocode: {
																								...this.props.playerPage.bonuses.allPromocode,
																								currentSearchType: option.detail.selectedOption.type,
																								currentOption: { ...this.props.playerPage.bonuses.allPromocode.currentOption, type: option.detail.selectedOption },
																							},
																						},
																					});
																			}}
																			options={[
																				{ value: "By UID", type: "uid" },
																				{ value: "By code", type: "code" },
																				{ value: "By value", type: "value" },
																				{ value: "By type", type: "type" },
																			]}
																		/>
																		<Input
																			disableBrowserAutocorrect={true}
																			placeholder="type, code, ip, note"
																			onKeyDown={(event) => this.handleKeyPress(event, "allPromocode")}
																			value={this.props.playerPage.bonuses.allPromocode.currentSearch}
																			onChange={(input) => {
																				this.handleInputDebounceChange(input, "allPromocode");
																			}}
																		/>
																	</>
																}
															/>
														}
														empty="No promocode found with requested filter"
													/>
												),
											},

											{
												id: "stats",
												label: "Stats",
												content: (
													<Table
														loading={this.state.userStats.loading}
														sortingColumn={{ sortingField: this.props.playerPage.sorting.sortingColumn.sortingField }}
														sortingDescending={this.props.playerPage.sorting.isDescending}
														onSortingChange={(sorting) => {
															this.props.setPlayerPage({ ...this.props.playerPage, sorting: sorting.detail });
															this.scheduleTimeout(() => {
																this.setState({
																	...this.state,
																	userStats: {
																		...this.state.userStats,
																		data: cloneDeep(this.state.userStats.data).sort((a, b) => {
																			return this.compare(a, b);
																		}),
																	},
																});
															}, 0);
														}}
														columnDefinitions={[
															{ id: "game", header: "Game", sortingField: "gameID", cell: (item) => this.props.Interface.getGameByID(item.gameID) },
															{
																id: "rounds",
																header: "Rounds played",
																sortingField: "count",
																cell: (item) => item.count,
															},
															{ id: "topwin", header: "Top win", sortingField: "top", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format((item.top / 100).toFixed(2))} RUB.</> },
															{ id: "betsamount", header: "Bets amount", sortingField: "bets", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format((item.bets / 100).toFixed(2))} RUB.</> },
															{ id: "rtp", header: "RTP", sortingField: "rtp", cell: (item) => <>{item.rtp}%</> },
															{ id: "last", header: "Last action", sortingField: "last", cell: (item) => new Date(item.last).toLocaleDateString() + " " + new Date(item.last).toLocaleTimeString() },
														]}
														items={
															this.props.playerPage.log.currentSearch
																? this.state.userStatsSearch.data.slice((this.props.playerPage.pagination.current - 1) * this.props.config.players.log.perPage, this.props.playerPage.pagination.current * this.props.config.players.log.perPage)
																: this.state.userStats.data.slice((this.props.playerPage.pagination.current - 1) * this.props.config.players.log.perPage, this.props.playerPage.pagination.current * this.props.config.players.log.perPage)
														}
														pagination={
															<Pagination
																currentPageIndex={this.props.playerPage.pagination.current}
																pagesCount={this.props.playerPage.pagination.total}
																onChange={(page) => {
																	this.props.setPlayerPage({ ...this.props.playerPage, pagination: { ...this.props.playerPage.pagination, current: page.detail.currentPageIndex } });
																}}
															/>
														}
														preferences={
															<Preferences
																cancelLabel="Cancel"
																confirmLabel="Confirm"
																pageSizePreference={{ title: "Logs per page", options: this.props.config.players.log.preferences.perPage }}
																preferences={{ pageSize: this.props.config.players.log.perPage }}
																title="Preferences"
																onConfirm={(preferences) => {
																	localStorage.setItem("players.log.perPage", preferences.detail.pageSize);
																	this.props.setConfig({ ...this.props.config, players: { ...this.props.config.players, log: { ...this.props.config.players.log, perPage: preferences.detail.pageSize } } });
																	this.props.setPlayerPage({ ...this.props.playerPage, pagination: { current: 1, total: Math.ceil(this.state.userStats.data.length / preferences.detail.pageSize) } });
																}}
															/>
														}
														filter={
															<Input
																disableBrowserAutocorrect={true}
																placeholder="Operation type, amount or something"
																value={this.props.playerPage.log.currentSearch}
																onChange={(input) => {
																	this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, currentSearch: input.detail.value } });
																	this.debounceFilter();
																}}
															/>
														}
														empty="No stats found with requested filter"
													/>
												),
											},
											{
												id: "freespins",
												label: "Freespins",
												content: (
													<div>
														<SpaceBetween direction="vertical" size="m">
															<Table
																loading={!this.props.playerPage.freespins?.loaded}
																columnDefinitions={[
																	{
																		id: "status",
																		header: "Status",
																		cell: (item) => {
																			const currentDate = new Date();
																			const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null;
																			const spinsLeft = item.spinsLeft || 0;

																			let status = item.status === "queue" ? "queue" : "active";
																			let color = item.status === "queue" ? "blue" : "green";

																			if (spinsLeft === 0) {
																				status = "completed";
																				color = "blue";
																			} else if (expiresAt && currentDate > expiresAt) {
																				status = "expired";
																				color = "red";
																			}

																			return <Badge color={color}>{status}</Badge>;
																		},
																	},
																	{
																		id: "bonus_name",
																		header: "Bonus Name",
																		cell: (item) => {
																			if (item.data && typeof item.data === "string") {
																				try {
																					const parsedData = JSON.parse(item.data);
																					return parsedData.bonusName || "Unknown";
																				} catch (e) {
																					return "Unknown";
																				}
																			}
																			return "Unknown";
																		},
																	},
																	{
																		id: "game_id",
																		header: "Game",
																		cell: (item) => {
																			const gameID = item.gameID;
																			if (gameID && this.props.game?.data?.length) {
																				const game = this.props.game.data.find((g) => g.gameID === gameID);
																				return game ? game.name : `Unknown Game (${gameID})`;
																			}
																			return gameID ? `Game ${gameID}` : "Unknown";
																		},
																	},
																	{
																		id: "spins_progress",
																		header: "Spins Progress",
																		cell: (item) => {
																			let total = 0;
																			if (item.data && typeof item.data === "string") {
																				try {
																					const parsedData = JSON.parse(item.data);
																					total = parseInt(parsedData.spinsCount) || 0;
																				} catch (e) {
																					total = 0;
																				}
																			}
																			const remaining = item.spinsLeft || 0;
																			const used = total - remaining;
																			return `${used}/${total}`;
																		},
																	},
																	{
																		id: "bet_level",
																		header: "Bet Level",
																		cell: (item) => {
																			let bet = 0;
																			if (item.data && typeof item.data === "string") {
																				try {
																					const parsedData = JSON.parse(item.data);
																					bet = parseInt(parsedData.totalBet) || 0;
																				} catch (e) {
																					bet = 0;
																				}
																			}
																			const currency = item.currency || "RUB";
																			return `${(bet / 1000).toFixed(2)} ${currency.toUpperCase()}`;
																		},
																	},
																	{
																		id: "created_at",
																		header: "Created",
																		cell: (item) => {
																			const date = item.createdAt;
																			return date ? new Date(date).toLocaleString("ru-RU") : "Unknown";
																		},
																	},
																	{
																		id: "expires_at",
																		header: "Expires",
																		cell: (item) => {
																			const expires = item.expiresAt;
																			return expires ? new Date(expires).toLocaleString("ru-RU") : "Never";
																		},
																	},
																]}
																items={(() => {
																	const data = this.props.playerPage.freespins?.data || [];
																	const filter = this.props.playerPage.freespins?.currentFilter || "all";

																	if (filter === "all") return data;

																	return data.filter((item) => {
																		const currentDate = new Date();
																		const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null;
																		const spinsLeft = item.spinsLeft || 0;

																		let status = item.status;
																		if (spinsLeft === 0) {
																			status = "completed";
																		} else if (expiresAt && currentDate > expiresAt) {
																			status = "expired";
																		}

																		return status === filter;
																	});
																})()}
																pagination={(() => {
																	const pagination = this.props.playerPage.freespins?.pagination;

																	if (!pagination || (!pagination.hasNext && !pagination.hasPrevious)) {
																		return null;
																	}

																	return (
																		<Pagination
																			currentPageIndex={pagination.currentPage}
																			pagesCount={pagination.currentPage + (pagination.hasNext ? 1 : 0)}
																			onChange={(event) => {
																				const currentPage = pagination.currentPage;
																				const targetPage = event.detail.currentPageIndex;

																				this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, targetPage, this.props.playerPage.uid, "active");
																			}}
																		/>
																	);
																})()}
																filter={this.renderFreespinsFilter()}
																preferences={this.renderFreespinsPreferences()}
																empty={this.renderFreespinsEmpty()}
															/>
														</SpaceBetween>
													</div>
												),
											},
											{
												id: "sweepstakes",
												label: `Sweepstakes ${this.props.playerPage.bonuses.sweepstakesActive.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.sweepstakesActive.pagination.count + ")"}`,
												content: (
													<>
														<Table
															filter={
																<>
																	{(this.props.user.level >= 6 || this.props.user.showColumn.includes("purchasedTickets")) && (
																		<Box margin={{ bottom: "m" }}>
																			<SpaceBetween direction="horizontal" size="xs">
																				<Box variant="span" fontWeight="bold">
																					Purchased tickets:
																				</Box>
																				{this.state.purchasedTickets.loading ? <Spinner size="normal" /> : <Box variant="span">{this.state.purchasedTickets.total}</Box>}
																			</SpaceBetween>
																		</Box>
																	)}
																</>
															}
															loading={!this.props.playerPage.bonuses.sweepstakesActive.loaded}
															sortingColumn={{ sortingField: this.props.playerPage.bonuses.sweepstakesActive.sorting.sortingColumn.column }}
															sortingDescending={this.props.playerPage.bonuses.sweepstakesActive.sorting.isDescending}
															onSortingChange={(sorting) => {
																this.props.setPlayerPage({
																	...this.props.playerPage,
																	bonuses: {
																		...this.props.playerPage.bonuses,
																		sweepstakesActive: {
																			...this.props.playerPage.bonuses.sweepstakesActive,
																			sorting: {
																				...this.props.playerPage.bonuses.sweepstakesActive.sorting,
																				sortingColumn: {
																					order: !sorting.detail.isDescending ? "ASC" : "DESC",
																					column: sorting.detail.sortingColumn.sortingField,
																				},
																				isDescending: sorting.detail.isDescending,
																			},
																		},
																	},
																});
																this.props.setPlayerPage({ ...this.props.playerPage, bonuses: { ...this.props.playerPage.bonuses, sweepstakesActive: { ...this.props.playerPage.bonuses.sweepstakesActive, pagination: { ...this.props.playerPage.bonuses.sweepstakesActive.pagination, current: 1 } } } });

																setTimeout(() => {
																	this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
																}, 250);
															}}
															columnDefinitions={[
																{ id: "id", header: "id", sortingField: "id", cell: (item) => (item.id ? item.id : "N/A") },
																{
																	id: "rewardType",
																	header: "Reward type",
																	cell: renderRewardType,
																},
																{
																	id: "reward value",
																	header: "Reward value",
																	cell: renderRewardValue,
																},
																{
																	id: "rewardConditions",
																	header: "Reward conditions",
																	cell: renderRewardConditions,
																},
																{
																	id: "status",
																	header: "Status",
																	cell: (item) => item.status,
																},
																{ id: "updatedAt", header: "Date", sortingField: "date", cell: (item) => new Date(item.updatedAt).toLocaleDateString() + " " + new Date(item.updatedAt).toLocaleTimeString() },
															]}
															items={this.props.playerPage.bonuses.sweepstakesActive.data}
															pagination={
																<Pagination
																	currentPageIndex={this.props.playerPage.bonuses.sweepstakesActive.pagination.current}
																	pagesCount={this.props.playerPage.bonuses.sweepstakesActive.pagination.total}
																	onChange={(page) => {
																		this.props.setPlayerPage({
																			...this.props.playerPage,
																			bonuses: {
																				...this.props.playerPage.bonuses,
																				sweepstakesActive: {
																					...this.props.playerPage.bonuses.sweepstakesActive,
																					pagination: {
																						...this.props.playerPage.bonuses.sweepstakesActive.pagination,
																						current: page.detail.currentPageIndex,
																					},
																				},
																			},
																		});

																		setTimeout(() => {
																			this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
																		}, 250);
																	}}
																/>
															}
															preferences={
																<Preferences
																	cancelLabel="Cancel"
																	confirmLabel="Confirm"
																	pageSizePreference={{ title: "Sweepstakes per page", options: this.props.config.players.bonuses.sweepstakesActive.preferences.perPage }}
																	preferences={{ pageSize: this.props.config.players.bonuses.sweepstakesActive.perPage }}
																	title="Preferences"
																	onConfirm={(preferences) => {
																		localStorage.setItem("players.bonuses.sweepstakesActive.perPage", preferences.detail.pageSize);

																		this.props.setConfig({
																			...this.props.config,
																			players: {
																				...this.props.config.players,
																				bonuses: {
																					...this.props.config.players.bonuses,
																					sweepstakesActive: {
																						...this.props.config.players.bonuses.sweepstakesActive,
																						perPage: preferences.detail.pageSize,
																					},
																				},
																			},
																		});

																		setTimeout(() => {
																			this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
																		}, 250);
																	}}
																/>
															}
															empty="No active sweepstakes found"
														/>
													</>
												),
											},
											{
												id: "achievements",
												label: `Achievements ${this.props.playerPage.bonuses.achievementsActive.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.achievementsActive.pagination.count + ")"}`,
												content: (
													<Table
														loading={!this.props.playerPage.bonuses.achievementsActive.loaded}
														sortingColumn={{ sortingField: this.props.playerPage.bonuses.achievementsActive.sorting.sortingColumn.column }}
														sortingDescending={this.props.playerPage.bonuses.achievementsActive.sorting.isDescending}
														onSortingChange={(sorting) => {
															this.props.setPlayerPage({
																...this.props.playerPage,
																bonuses: {
																	...this.props.playerPage.bonuses,
																	achievementsActive: {
																		...this.props.playerPage.bonuses.achievementsActive,
																		sorting: {
																			...this.props.playerPage.bonuses.achievementsActive.sorting,
																			sortingColumn: {
																				order: !sorting.detail.isDescending ? "ASC" : "DESC",
																				column: sorting.detail.sortingColumn.sortingField,
																			},
																			isDescending: sorting.detail.isDescending,
																		},
																	},
																},
															});

															setTimeout(() => {
																this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
															}, 250);
														}}
														columnDefinitions={[
															{ id: "id", header: "ID", sortingField: "instanceId", cell: (item) => (item.instanceId ? item.instanceId : "N/A") },
															{ id: "name", header: "Name", cell: (item) => (item.name ? item.name : "N/A") },
															{ id: "description", header: "Description", cell: (item) => (item.description ? item.description : "N/A") },
															{ id: "type", header: "Type", cell: (item) => (item.type ? item.type : "N/A") },
															{ id: "sourceType", header: "Source", cell: (item) => (item.sourceType ? item.sourceType : "N/A") },
															{
																id: "status",
																header: "Status",
																cell: (item) => (item.status ? item.status : "N/A"),
															},
															{
																id: "progress",
																header: "Progress",
																cell: (item) => (item.type === "progressive" ? `${item.progress || 0}/${item.count || 0} (Level ${item.level || 1}/${item.totalLevels || 1})` : `${item.progress || 0}/${item.count || 0}`),
															},
															{
																id: "rewardType",
																header: "Reward type",
																cell: renderRewardType,
															},
															{
																id: "rewardValue",
																header: "Reward value",
																cell: renderRewardValue,
															},
															{ id: "startedAt", header: "Started", cell: (item) => (item.startedAt ? new Date(item.startedAt).toLocaleDateString() + " " + new Date(item.startedAt).toLocaleTimeString() : "N/A") },
															{ id: "updatedAt", header: "Updated", sortingField: "updatedAt", cell: (item) => (item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() + " " + new Date(item.updatedAt).toLocaleTimeString() : "N/A") },
															{ id: "claimedAt", header: "Claimed", cell: (item) => (item.claimedAt ? new Date(item.claimedAt).toLocaleDateString() + " " + new Date(item.claimedAt).toLocaleTimeString() : "N/A") },
														]}
														items={this.props.playerPage.bonuses.achievementsActive.data}
														pagination={
															<Pagination
																currentPageIndex={this.props.playerPage.bonuses.achievementsActive.pagination.current}
																pagesCount={this.props.playerPage.bonuses.achievementsActive.pagination.total}
																onChange={(page) => {
																	this.props.setPlayerPage({
																		...this.props.playerPage,
																		bonuses: {
																			...this.props.playerPage.bonuses,
																			achievementsActive: {
																				...this.props.playerPage.bonuses.achievementsActive,
																				pagination: {
																					...this.props.playerPage.bonuses.achievementsActive.pagination,
																					current: page.detail.currentPageIndex,
																				},
																			},
																		},
																	});
																	setTimeout(() => {
																		this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
																	}, 250);
																}}
															/>
														}
														preferences={
															<Preferences
																cancelLabel="Cancel"
																confirmLabel="Confirm"
																pageSizePreference={{ title: "Achievements per page", options: this.props.config.players.bonuses.achievementsActive.preferences.perPage }}
																preferences={{ pageSize: this.props.config.players.bonuses.achievementsActive.perPage }}
																title="Preferences"
																onConfirm={(preferences) => {
																	localStorage.setItem("players.bonuses.achievementsActive.perPage", preferences.detail.pageSize);

																	this.props.setConfig({
																		...this.props.config,
																		players: {
																			...this.props.config.players,
																			bonuses: {
																				...this.props.config.players.bonuses,
																				achievementsActive: {
																					...this.props.config.players.bonuses.achievementsActive,
																					perPage: preferences.detail.pageSize,
																				},
																			},
																		},
																	});

																	setTimeout(() => {
																		this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "active");
																	}, 250);
																}}
															/>
														}
														empty="No active achievements found"
													/>
												),
											},
											{
												id: "bonuses",
												label: "History",
												content: (
													<Tabs
														onChange={(tab) => {
															this.switchTab(tab.detail.activeTabId);
															if (tab.detail.activeTabId === "fortuneWheel") {
																if (this.props.playerPage.bonuses.fortuneWheel.data.length === 0) {
																	setTimeout(() => {
																		this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid);
																	}, 250);
																}
															}

															if (tab.detail.activeTabId === "promocode") {
																if (this.props.playerPage.bonuses.promocode.data.length === 0) {
																	setTimeout(() => {
																		this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project);
																	}, 250);
																}
															}

															if (tab.detail.activeTabId === "rain") {
																if (this.props.playerPage.bonuses.rain.data.length === 0) {
																	setTimeout(() => {
																		this.props.Interface.loadUserRain(this.props.playerPage.uid, this.props.playerPage.data.project);
																	}, 250);
																}
															}

															if (tab.detail.activeTabId === "sweepstakes_history") {
																if (this.props.playerPage.bonuses.sweepstakesHistory.data.length === 0) {
																	setTimeout(() => {
																		this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																	}, 250);
																}
															}

															if (tab.detail.activeTabId === "achievements_history") {
																if (this.props.playerPage.bonuses.achievementsHistory.data.length === 0) {
																	setTimeout(() => {
																		this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																	}, 250);
																}
															}

															if (tab.detail.activeTabId === "freespins_history") {
																this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, 1, this.props.playerPage.uid, "history");
															}

															if (tab.detail.activeTabId === "sportsbook_freebets") {
																this.props.Interface.loadSportsbookFreebets(this.props.playerPage.data.casinoID, this.props.playerPage.uid, this.props.playerPage.data.project);
															}
														}}
														tabs={[
															{
																id: "fortuneWheel",
																label: `Fortune wheel ${this.props.playerPage.bonuses.fortuneWheel.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.fortuneWheel.pagination.count + ")"}`,
																content: (
																	<Table
																		loading={!this.props.playerPage.bonuses.fortuneWheel.loaded}
																		sortingColumn={{ sortingField: this.props.playerPage.bonuses.fortuneWheel.sorting.sortingColumn.column }}
																		sortingDescending={this.props.playerPage.bonuses.fortuneWheel.sorting.isDescending}
																		onSortingChange={(sorting) => {
																			this.props.setPlayerPage({
																				...this.props.playerPage,
																				bonuses: {
																					...this.props.playerPage.bonuses,
																					fortuneWheel: {
																						...this.props.playerPage.bonuses.fortuneWheel,
																						sorting: {
																							...this.props.playerPage.bonuses.fortuneWheel.sorting,
																							sortingColumn: {
																								order: !sorting.detail.isDescending ? "ASC" : "DESC",
																								column: sorting.detail.sortingColumn.sortingField,
																							},
																							isDescending: sorting.detail.isDescending,
																						},
																					},
																				},
																			});

																			setTimeout(() => {
																				this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid);
																			}, 250);
																		}}
																		columnDefinitions={[
																			{
																				id: "wheelId",
																				header: "wheelId",
																				sortingField: "wheelId",
																				cell: (item) => (
																					<Button
																						iconName={"status-in-progress"}
																						className={[this.props.styles["btnUID"], typeof document.body.classList[0] !== "undefined" ? this.props.styles["dark"] : "", this.props.styles["blue"]].join(" ")}
																						variant="normal"
																						onClick={() => {
																							this.props.Interface.loadInfoFortuneWheel(item.wheelId);

																							this.props.setFortuneWheelModal({ ...this.props.fortuneWheelModal, show: false });

																							setTimeout(() => {
																								this.props.Interface.loadInfoFortuneWheel(item.wheelId);
																							}, 250);
																						}}
																						children={item.wheelId}
																					/>
																				),
																			},
																			{
																				id: "amount",
																				header: "amount",
																				sortingField: "amount",
																				cell: (item) => (
																					<Box variant="span" color={item.amount >= 0 ? (item.amount > 0 ? "text-status-success" : "text-body-secondary") : "text-status-error"}>
																						{new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.amount / 100)} RUB.
																					</Box>
																				),
																			},
																			{ id: "ip", header: "ip", cell: (item) => item.ip },
																			{ id: "date", header: "date", sortingField: "date", cell: (item) => new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString() },
																		]}
																		items={this.props.playerPage.bonuses.fortuneWheel.data}
																		pagination={
																			<Pagination
																				currentPageIndex={this.props.playerPage.bonuses.fortuneWheel.pagination.current}
																				pagesCount={this.props.playerPage.bonuses.fortuneWheel.pagination.total}
																				onChange={(page) => {
																					this.props.setPlayerPage({
																						...this.props.playerPage,
																						bonuses: {
																							...this.props.playerPage.bonuses,
																							fortuneWheel: {
																								...this.props.playerPage.bonuses.fortuneWheel,
																								pagination: {
																									...this.props.playerPage.bonuses.fortuneWheel.pagination,
																									current: page.detail.currentPageIndex,
																								},
																							},
																						},
																					});
																					setTimeout(() => {
																						this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid);
																					}, 250);
																				}}
																			/>
																		}
																		preferences={
																			<Preferences
																				cancelLabel="Cancel"
																				confirmLabel="Confirm"
																				pageSizePreference={{ title: "Bonuses fortuneWheel per page", options: this.props.config.players.bonuses.fortuneWheel.preferences.perPage }}
																				preferences={{ pageSize: this.props.config.players.bonuses.fortuneWheel.perPage }}
																				title="Preferences"
																				onConfirm={(preferences) => {
																					localStorage.setItem("players.bonuses.fortuneWheel.perPage", preferences.detail.pageSize);
																					this.props.setConfig({
																						...this.props.config,
																						players: {
																							...this.props.config.players,
																							bonuses: {
																								...this.props.config.players.bonuses,
																								fortuneWheel: {
																									...this.props.config.players.bonuses.fortuneWheel,
																									perPage: preferences.detail.pageSize,
																								},
																							},
																						},
																					});

																					setTimeout(() => {
																						this.props.Interface.loadFortuneWheelWinner(this.props.playerPage.uid);
																					}, 250);
																				}}
																			/>
																		}
																		filter={
																			<Input
																				disableBrowserAutocorrect={true}
																				placeholder="Amount or ip"
																				onKeyDown={(event) => this.handleKeyPress(event, "winner")}
																				value={this.props.playerPage.bonuses.fortuneWheel.currentSearch}
																				onChange={(input) => {
																					this.handleInputDebounceChange(input, "winner");
																				}}
																			/>
																		}
																		empty="No fortune wheel found with requested filter"
																	/>
																),
															},
															{
																id: "promocode",
																label: `Promocode ${this.props.playerPage.bonuses.promocode.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.promocode.pagination.count + ")"}`,
																content: (
																	<Table
																		loading={!this.props.playerPage.bonuses.promocode.loaded}
																		sortingColumn={{ sortingField: this.props.playerPage.bonuses.promocode.sorting.sortingColumn.column }}
																		sortingDescending={this.props.playerPage.bonuses.promocode.sorting.isDescending}
																		onSortingChange={(sorting) => {
																			this.props.setPlayerPage({
																				...this.props.playerPage,
																				bonuses: {
																					...this.props.playerPage.bonuses,
																					promocode: {
																						...this.props.playerPage.bonuses.promocode,
																						sorting: {
																							...this.props.playerPage.bonuses.promocode.sorting,
																							sortingColumn: {
																								order: !sorting.detail.isDescending ? "ASC" : "DESC",
																								column: sorting.detail.sortingColumn.sortingField,
																							},
																							isDescending: sorting.detail.isDescending,
																						},
																					},
																				},
																			});

																			setTimeout(() => {
																				this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project);
																			}, 250);
																		}}
																		columnDefinitions={[
																			{ id: "data_code", header: "code", cell: (item) => item.data_code },
																			{ id: "type", header: "type", sortingField: "type", cell: (item) => item.type },
																			{ id: "note", header: "note", cell: (item) => item.note },
																			{ id: "ip", header: "ip", cell: (item) => item.ip },
																			{ id: "date", header: "date", sortingField: "date", cell: (item) => new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString() },
																		]}
																		items={this.props.playerPage.bonuses.promocode.data}
																		pagination={
																			<Pagination
																				currentPageIndex={this.props.playerPage.bonuses.promocode.pagination.current}
																				pagesCount={this.props.playerPage.bonuses.promocode.pagination.total}
																				onChange={(page) => {
																					this.props.setPlayerPage({
																						...this.props.playerPage,
																						bonuses: {
																							...this.props.playerPage.bonuses,
																							promocode: {
																								...this.props.playerPage.bonuses.promocode,
																								pagination: {
																									...this.props.playerPage.bonuses.promocode.pagination,
																									current: page.detail.currentPageIndex,
																								},
																							},
																						},
																					});
																					setTimeout(() => {
																						this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project);
																					}, 250);
																				}}
																			/>
																		}
																		preferences={
																			<Preferences
																				cancelLabel="Cancel"
																				confirmLabel="Confirm"
																				pageSizePreference={{ title: "Bonuses promocode per page", options: this.props.config.players.bonuses.promocode.preferences.perPage }}
																				preferences={{ pageSize: this.props.config.players.bonuses.promocode.perPage }}
																				title="Preferences"
																				onConfirm={(preferences) => {
																					localStorage.setItem("players.bonuses.promocode.perPage", preferences.detail.pageSize);
																					this.props.setConfig({
																						...this.props.config,
																						players: {
																							...this.props.config.players,
																							bonuses: {
																								...this.props.config.players.bonuses,
																								promocode: {
																									...this.props.config.players.bonuses.promocode,
																									perPage: preferences.detail.pageSize,
																								},
																							},
																						},
																					});

																					setTimeout(() => {
																						this.props.Interface.loadUserPromocodes(this.props.playerPage.uid, this.props.playerPage.data.project);
																					}, 250);
																				}}
																			/>
																		}
																		filter={
																			<Input
																				disableBrowserAutocorrect={true}
																				placeholder="type, code, ip, note"
																				onKeyDown={(event) => this.handleKeyPress(event, "promocode")}
																				value={this.props.playerPage.bonuses.promocode.currentSearch}
																				onChange={(input) => {
																					this.handleInputDebounceChange(input, "promocode");
																				}}
																			/>
																		}
																		empty="No promocode found with requested filter"
																	/>
																),
															},
															{
																id: "freespins_history",
																label: "Freespins",
																content: (
																	<div>
																		<SpaceBetween direction="vertical" size="m">
																			<Table
																				loading={!this.props.playerPage.freespins?.loaded}
																				columnDefinitions={[
																					{
																						id: "status",
																						header: "Status",
																						cell: (item) => {
																							const currentDate = new Date();
																							const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null;
																							const spinsLeft = item.spinsLeft || 0;

																							let status = "active";
																							let color = "green";

																							if (spinsLeft === 0) {
																								status = "completed";
																								color = "blue";
																							} else if (expiresAt && currentDate > expiresAt) {
																								status = "expired";
																								color = "red";
																							}

																							return <Badge color={color}>{status}</Badge>;
																						},
																					},
																					{
																						id: "bonus_name",
																						header: "Bonus Name",
																						cell: (item) => {
																							if (item.data && typeof item.data === "string") {
																								try {
																									const parsedData = JSON.parse(item.data);
																									return parsedData.bonusName || "Unknown";
																								} catch (e) {
																									return "Unknown";
																								}
																							}
																							return "Unknown";
																						},
																					},
																					{
																						id: "game_id",
																						header: "Game",
																						cell: (item) => {
																							const gameID = item.gameID;
																							if (gameID && this.props.game?.data?.length) {
																								const game = this.props.game.data.find((g) => g.gameID === gameID);
																								return game ? game.name : `Unknown Game (${gameID})`;
																							}
																							return gameID ? `Game ${gameID}` : "Unknown";
																						},
																					},
																					{
																						id: "spins_progress",
																						header: "Spins Progress",
																						cell: (item) => {
																							let total = 0;
																							if (item.data && typeof item.data === "string") {
																								try {
																									const parsedData = JSON.parse(item.data);
																									total = parseInt(parsedData.spinsCount) || 0;
																								} catch (e) {
																									total = 0;
																								}
																							}
																							const remaining = item.spinsLeft || 0;
																							const used = total - remaining;
																							return `${used}/${total}`;
																						},
																					},
																					{
																						id: "bet_level",
																						header: "Bet Level",
																						cell: (item) => {
																							let bet = 0;
																							if (item.data && typeof item.data === "string") {
																								try {
																									const parsedData = JSON.parse(item.data);
																									bet = parseInt(parsedData.totalBet) || 0;
																								} catch (e) {
																									bet = 0;
																								}
																							}
																							const currency = item.currency || "RUB";
																							return `${(bet / 1000).toFixed(2)} ${currency.toUpperCase()}`;
																						},
																					},
																					{
																						id: "created_at",
																						header: "Created",
																						cell: (item) => {
																							const date = item.createdAt;
																							return date ? new Date(date).toLocaleString("ru-RU") : "Unknown";
																						},
																					},
																					{
																						id: "expires_at",
																						header: "Expires",
																						cell: (item) => {
																							const expires = item.expiresAt;
																							return expires ? new Date(expires).toLocaleString("ru-RU") : "Never";
																						},
																					},
																				]}
																				items={(() => {
																					const data = this.props.playerPage.freespins?.data || [];
																					const filter = this.props.playerPage.freespins?.currentFilter || "all";

																					if (filter === "all") return data;

																					return data.filter((item) => {
																						const currentDate = new Date();
																						const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null;
																						const spinsLeft = item.spinsLeft || 0;

																						let status = "active";
																						if (spinsLeft === 0) {
																							status = "completed";
																						} else if (expiresAt && currentDate > expiresAt) {
																							status = "expired";
																						}

																						return status === filter;
																					});
																				})()}
																				pagination={(() => {
																					const pagination = this.props.playerPage.freespins?.pagination;

																					if (!pagination || (!pagination.hasNext && !pagination.hasPrevious)) {
																						return null;
																					}

																					return (
																						<Pagination
																							currentPageIndex={pagination.currentPage}
																							pagesCount={pagination.currentPage + (pagination.hasNext ? 1 : 0)}
																							onChange={(event) => {
																								const currentPage = pagination.currentPage;
																								const targetPage = event.detail.currentPageIndex;

																								this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, targetPage, this.props.playerPage.uid, "active");
																							}}
																						/>
																					);
																				})()}
																				filter={
																					<Grid
																						gridDefinition={[{ colspan: 3 }]}
																						children={
																							<Select
																								filteringType="auto"
																								disabled={!this.props.playerPage.freespins?.loaded}
																								placeholder="Status"
																								selectedOption={
																									this.props.playerPage.freespins?.currentFilter && this.props.playerPage.freespins?.currentFilter !== "all"
																										? {
																												value: this.props.playerPage.freespins.currentFilter,
																												label: this.props.playerPage.freespins.currentFilter === "active" ? "Active" : this.props.playerPage.freespins.currentFilter === "completed" ? "Completed" : "Expired",
																										  }
																										: null
																								}
																								onChange={(option) => {
																									const newFilter = option.detail.selectedOption?.value || "all";
																									this.props.setPlayerPage({
																										...this.props.playerPage,
																										freespins: {
																											...this.props.playerPage.freespins,
																											currentFilter: newFilter,
																										},
																									});
																								}}
																								options={[
																									{ value: "all", label: "All" },
																									{ value: "active", label: "Active" },
																									{ value: "completed", label: "Completed" },
																									{ value: "expired", label: "Expired" },
																								]}
																							/>
																						}
																					/>
																				}
																				preferences={
																					<Preferences
																						cancelLabel="Cancel"
																						confirmLabel="Confirm"
																						pageSizePreference={{
																							title: "Freespins per page",
																							options: this.props.config.freespins.preferences.perPage,
																						}}
																						preferences={{ pageSize: this.props.config.freespins.perPage }}
																						title="Preferences"
																						onConfirm={(preferences) => {
																							localStorage.setItem("freespins.perPage", preferences.detail.pageSize);
																							this.props.setConfig({
																								...this.props.config,
																								freespins: {
																									...this.props.config.freespins,
																									perPage: preferences.detail.pageSize,
																								},
																							});
																							this.props.setPlayerPage({
																								...this.props.playerPage,
																								freespins: {
																									...this.props.playerPage.freespins,
																									pagination: {
																										...this.props.playerPage.freespins.pagination,
																										currentPage: 1,
																									},
																								},
																							});
																							setTimeout(() => {
																								this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, 1, this.props.playerPage.uid, "active");
																							}, 100);
																						}}
																					/>
																				}
																				empty={
																					<Box textAlign="center" color="inherit">
																						<div>
																							<Box variant="strong" textAlign="center" color="inherit">
																								No freespins found
																							</Box>
																							<Box variant="p" padding={{ bottom: "s" }} color="inherit">
																								This player has no freespins yet.
																							</Box>
																						</div>
																					</Box>
																				}
																			/>
																		</SpaceBetween>
																	</div>
																),
															},
															{
																id: "sportsbook_freebets",
																label: `Sportsbook freebets ${this.props.playerPage.sportsbookFreebets.data.length === 0 ? "" : "(" + this.props.playerPage.sportsbookFreebets.data.length + ")"}`,
																content: (
																	<Table
																		loading={!this.props.playerPage.sportsbookFreebets.loaded}
																		loadingText="Loading sportsbook freebets"
																		items={this.props.playerPage.sportsbookFreebets.data}
																		columnDefinitions={[
																			{
																				id: "bonusId",
																				header: "Bonus ID",
																				cell: (item) => item.bonusId,
																			},
																			{
																				id: "bonusName",
																				header: "Bonus name",
																				cell: (item) => item.bonusName || "-",
																			},
																			{
																				id: "amount",
																				header: "Amount",
																				cell: (item) => `${Number(item.amount || 0).toLocaleString("ru-RU")} ${item.currency || ""}`.trim(),
																			},
																			{
																				id: "createdAt",
																				header: "Created at",
																				cell: (item) => (item.createdAt ? new Date(item.createdAt).toLocaleString("ru-RU") : "-"),
																			},
																			{
																				id: "expiredAt",
																				header: "Expired at",
																				cell: (item) => (item.expiredAt ? new Date(item.expiredAt).toLocaleString("ru-RU") : "-"),
																			},
																		]}
																		empty={
																			<Box textAlign="center" color="inherit">
																				<Box variant="strong" textAlign="center" color="inherit">
																					{typeof this.props.playerPage.sportsbookFreebets.error === "string" ? this.props.playerPage.sportsbookFreebets.error : "No sportsbook freebets found"}
																				</Box>
																			</Box>
																		}
																	/>
																),
															},
															{
																id: "rain",
																label: `Rain ${this.props.playerPage.bonuses.rain.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.rain.pagination.count + ")"}`,
																content: (
																	<Table
																		loading={!this.props.playerPage.bonuses.rain.loaded}
																		sortingColumn={{ sortingField: this.props.playerPage.bonuses.rain.sorting.sortingColumn.column }}
																		sortingDescending={this.props.playerPage.bonuses.rain.sorting.isDescending}
																		onSortingChange={(sorting) => {
																			this.props.setPlayerPage({
																				...this.props.playerPage,
																				bonuses: {
																					...this.props.playerPage.bonuses,
																					rain: {
																						...this.props.playerPage.bonuses.rain,
																						sorting: {
																							...this.props.playerPage.bonuses.rain.sorting,
																							sortingColumn: {
																								order: !sorting.detail.isDescending ? "ASC" : "DESC",
																								column: sorting.detail.sortingColumn.sortingField,
																							},
																							isDescending: sorting.detail.isDescending,
																						},
																					},
																				},
																			});

																			setTimeout(() => {
																				this.props.Interface.loadUserRain(this.props.playerPage.uid, this.props.playerPage.data.project);
																			}, 250);
																		}}
																		columnDefinitions={[
																			{
																				id: "uid",
																				header: "uid",
																				sortingField: "uid",
																				cell: (item) => (
																					<>
																						<Button
																							iconName={"status-in-progress"}
																							className={[this.props.styles["btnUID"], typeof document.body.classList[0] !== "undefined" ? this.props.styles["dark"] : "", item.uid === 0 ? this.props.styles["silver"] : this.props.styles["blue"]].join(" ")}
																							variant="normal"
																							disabled={item.uid === 0}
																							onClick={() => {
																								this.props.Interface.loadInfo(item.uid);
																							}}
																							children={item.uid === 0 ? "system" : item.uid}
																						/>
																					</>
																				),
																			},
																			{
																				id: "amount",
																				header: "amount",
																				cell: (item) => (
																					<Box variant="span" color={item.value >= 0 ? (item.value > 0 ? "text-status-success" : "text-body-secondary") : "text-status-error"}>
																						{new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.value / item.count / 100)} RUB.
																					</Box>
																				),
																			},
																			{
																				id: "total",
																				header: "total",
																				sortingField: "value",
																				cell: (item) => (
																					<Box variant="span" color={item.value >= 0 ? (item.value > 0 ? "text-status-success" : "text-body-secondary") : "text-status-error"}>
																						{new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.value / 100)} RUB.
																					</Box>
																				),
																			},
																			{ id: "date", header: "date", sortingField: "date", cell: (item) => new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString() },
																		]}
																		items={this.props.playerPage.bonuses.rain.data}
																		pagination={
																			<Pagination
																				currentPageIndex={this.props.playerPage.bonuses.rain.pagination.current}
																				pagesCount={this.props.playerPage.bonuses.rain.pagination.total}
																				onChange={(page) => {
																					this.props.setPlayerPage({
																						...this.props.playerPage,
																						bonuses: {
																							...this.props.playerPage.bonuses,
																							rain: {
																								...this.props.playerPage.bonuses.rain,
																								pagination: {
																									...this.props.playerPage.bonuses.rain.pagination,
																									current: page.detail.currentPageIndex,
																								},
																							},
																						},
																					});
																					setTimeout(() => {
																						this.props.Interface.loadUserRain(this.props.playerPage.uid, this.props.playerPage.data.project);
																					}, 250);
																				}}
																			/>
																		}
																		preferences={
																			<Preferences
																				cancelLabel="Cancel"
																				confirmLabel="Confirm"
																				pageSizePreference={{ title: "Bonuses rain per page", options: this.props.config.players.bonuses.rain.preferences.perPage }}
																				preferences={{ pageSize: this.props.config.players.bonuses.rain.perPage }}
																				title="Preferences"
																				onConfirm={(preferences) => {
																					localStorage.setItem("players.bonuses.rain.perPage", preferences.detail.pageSize);

																					this.props.setConfig({
																						...this.props.config,
																						players: {
																							...this.props.config.players,
																							bonuses: {
																								...this.props.config.players.bonuses,
																								rain: {
																									...this.props.config.players.bonuses.rain,
																									perPage: preferences.detail.pageSize,
																								},
																							},
																						},
																					});

																					setTimeout(() => {
																						this.props.Interface.loadUserRain(this.props.playerPage.uid, this.props.playerPage.data.project);
																					}, 250);
																				}}
																			/>
																		}
																		filter={
																			<Input
																				disableBrowserAutocorrect={true}
																				placeholder="uid, total"
																				onKeyDown={(event) => this.handleKeyPress(event, "rain")}
																				value={this.props.playerPage.bonuses.rain.currentSearch}
																				onChange={(input) => {
																					this.handleInputDebounceChange(input, "rain");
																				}}
																			/>
																		}
																		empty="No rain found with requested filter"
																	/>
																),
															},
															{
																id: "sweepstakes_history",
																label: `Sweepstakes ${this.props.playerPage.bonuses.sweepstakesHistory.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.sweepstakesHistory.pagination.count + ")"}`,
																content: (
																	<Table
																		loading={!this.props.playerPage.bonuses.sweepstakesHistory.loaded}
																		sortingColumn={{ sortingField: this.props.playerPage.bonuses.sweepstakesHistory.sorting.sortingColumn.column }}
																		sortingDescending={this.props.playerPage.bonuses.sweepstakesHistory.sorting.isDescending}
																		onSortingChange={(sorting) => {
																			this.props.setPlayerPage({
																				...this.props.playerPage,
																				bonuses: {
																					...this.props.playerPage.bonuses,
																					sweepstakesHistory: {
																						...this.props.playerPage.bonuses.sweepstakesHistory,
																						sorting: {
																							...this.props.playerPage.bonuses.sweepstakesHistory.sorting,
																							sortingColumn: {
																								order: !sorting.detail.isDescending ? "ASC" : "DESC",
																								column: sorting.detail.sortingColumn.sortingField,
																							},
																							isDescending: sorting.detail.isDescending,
																						},
																					},
																				},
																			});

																			setTimeout(() => {
																				this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																			}, 250);
																		}}
																		columnDefinitions={[
																			{ id: "id", header: "id", sortingField: "id", cell: (item) => (item.id ? item.id : "N/A") },
																			{
																				id: "source",
																				header: "Source",
																				cell: (item) => (item.historyType === "rtp_gift" ? "RTP gift" : "Round"),
																			},
																			{
																				id: "rewardType",
																				header: "Reward type",
																				cell: renderRewardType,
																			},
																			{
																				id: "reward value",
																				header: "Reward value",
																				cell: renderRewardValue,
																			},
																			{
																				id: "rewardConditions",
																				header: "Reward conditions",
																				cell: renderRewardConditions,
																			},
																			{
																				id: "status",
																				header: "Status",
																				cell: (item) => item.status,
																			},
																			{ id: "updatedAt", header: "Date", sortingField: "date", cell: (item) => new Date(item.updatedAt).toLocaleDateString() + " " + new Date(item.updatedAt).toLocaleTimeString() },
																		]}
																		items={this.props.playerPage.bonuses.sweepstakesHistory.data}
																		pagination={
																			<Pagination
																				currentPageIndex={this.props.playerPage.bonuses.sweepstakesHistory.pagination.current}
																				pagesCount={this.props.playerPage.bonuses.sweepstakesHistory.pagination.total}
																				onChange={(page) => {
																					this.props.setPlayerPage({
																						...this.props.playerPage,
																						bonuses: {
																							...this.props.playerPage.bonuses,
																							sweepstakesHistory: {
																								...this.props.playerPage.bonuses.sweepstakesHistory,
																								pagination: {
																									...this.props.playerPage.bonuses.sweepstakesHistory.pagination,
																									current: page.detail.currentPageIndex,
																								},
																							},
																						},
																					});
																					setTimeout(() => {
																						this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																					}, 250);
																				}}
																			/>
																		}
																		preferences={
																			<Preferences
																				cancelLabel="Cancel"
																				confirmLabel="Confirm"
																				pageSizePreference={{ title: "Sweepstakes History per page", options: this.props.config.players.bonuses.sweepstakesHistory.preferences.perPage }}
																				preferences={{ pageSize: this.props.config.players.bonuses.sweepstakesHistory.perPage }}
																				title="Preferences"
																				onConfirm={(preferences) => {
																					localStorage.setItem("players.bonuses.sweepstakesHistory.perPage", preferences.detail.pageSize);

																					this.props.setConfig({
																						...this.props.config,
																						players: {
																							...this.props.config.players,
																							bonuses: {
																								...this.props.config.players.bonuses,
																								sweepstakesHistory: {
																									...this.props.config.players.bonuses.sweepstakesHistory,
																									perPage: preferences.detail.pageSize,
																								},
																							},
																						},
																					});

																					setTimeout(() => {
																						this.props.Interface.loadSweepStakes(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																					}, 250);
																				}}
																			/>
																		}
																		empty="No sweepstakes history found"
																	/>
																),
															},
															{
																id: "achievements_history",
																label: `Achievements ${this.props.playerPage.bonuses.achievementsHistory.pagination.count === 0 ? "" : "(" + this.props.playerPage.bonuses.achievementsHistory.pagination.count + ")"}`,
																content: (
																	<Table
																		loading={!this.props.playerPage.bonuses.achievementsHistory.loaded}
																		sortingColumn={{ sortingField: this.props.playerPage.bonuses.achievementsHistory.sorting.sortingColumn.column }}
																		sortingDescending={this.props.playerPage.bonuses.achievementsHistory.sorting.isDescending}
																		onSortingChange={(sorting) => {
																			this.props.setPlayerPage({
																				...this.props.playerPage,
																				bonuses: {
																					...this.props.playerPage.bonuses,
																					achievementsHistory: {
																						...this.props.playerPage.bonuses.achievementsHistory,
																						sorting: {
																							...this.props.playerPage.bonuses.achievementsHistory.sorting,
																							sortingColumn: {
																								order: !sorting.detail.isDescending ? "ASC" : "DESC",
																								column: sorting.detail.sortingColumn.sortingField,
																							},
																							isDescending: sorting.detail.isDescending,
																						},
																					},
																				},
																			});

																			setTimeout(() => {
																				this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																			}, 250);
																		}}
																		columnDefinitions={[
																			{ id: "id", header: "ID", sortingField: "instanceId", cell: (item) => (item.instanceId ? item.instanceId : "N/A") },
																			{ id: "name", header: "Name", cell: (item) => (item.name ? item.name : "N/A") },
																			{ id: "description", header: "Description", cell: (item) => (item.description ? item.description : "N/A") },
																			{ id: "type", header: "Type", cell: (item) => (item.type ? item.type : "N/A") },
																			{ id: "sourceType", header: "Source", cell: (item) => (item.sourceType ? item.sourceType : "N/A") },
																			{
																				id: "status",
																				header: "Status",
																				cell: (item) => (item.status ? item.status : "N/A"),
																			},
																			{
																				id: "progress",
																				header: "Progress",
																				cell: (item) => (item.type === "progressive" ? `${item.progress || 0}/${item.count || 0} (Level ${item.level || 1}/${item.totalLevels || 1})` : `${item.progress || 0}/${item.count || 0}`),
																			},
																			{
																				id: "rewardType",
																				header: "Reward type",
																				cell: renderRewardType,
																			},
																			{
																				id: "rewardValue",
																				header: "Reward value",
																				cell: renderRewardValue,
																			},
																			{ id: "startedAt", header: "Started", cell: (item) => (item.startedAt ? new Date(item.startedAt).toLocaleDateString() + " " + new Date(item.startedAt).toLocaleTimeString() : "N/A") },
																			{ id: "updatedAt", header: "Updated", sortingField: "updatedAt", cell: (item) => (item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() + " " + new Date(item.updatedAt).toLocaleTimeString() : "N/A") },
																			{ id: "claimedAt", header: "Claimed", cell: (item) => (item.claimedAt ? new Date(item.claimedAt).toLocaleDateString() + " " + new Date(item.claimedAt).toLocaleTimeString() : "N/A") },
																		]}
																		items={this.props.playerPage.bonuses.achievementsHistory.data}
																		pagination={
																			<Pagination
																				currentPageIndex={this.props.playerPage.bonuses.achievementsHistory.pagination.current}
																				pagesCount={this.props.playerPage.bonuses.achievementsHistory.pagination.total}
																				onChange={(page) => {
																					this.props.setPlayerPage({
																						...this.props.playerPage,
																						bonuses: {
																							...this.props.playerPage.bonuses,
																							achievementsHistory: {
																								...this.props.playerPage.bonuses.achievementsHistory,
																								pagination: {
																									...this.props.playerPage.bonuses.achievementsHistory.pagination,
																									current: page.detail.currentPageIndex,
																								},
																							},
																						},
																					});
																					setTimeout(() => {
																						this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																					}, 250);
																				}}
																			/>
																		}
																		preferences={
																			<Preferences
																				cancelLabel="Cancel"
																				confirmLabel="Confirm"
																				pageSizePreference={{ title: "Achievements History per page", options: this.props.config.players.bonuses.achievementsHistory.preferences.perPage }}
																				preferences={{ pageSize: this.props.config.players.bonuses.achievementsHistory.perPage }}
																				title="Preferences"
																				onConfirm={(preferences) => {
																					localStorage.setItem("players.bonuses.achievementsHistory.perPage", preferences.detail.pageSize);

																					this.props.setConfig({
																						...this.props.config,
																						players: {
																							...this.props.config.players,
																							bonuses: {
																								...this.props.config.players.bonuses,
																								achievementsHistory: {
																									...this.props.config.players.bonuses.achievementsHistory,
																									perPage: preferences.detail.pageSize,
																								},
																							},
																						},
																					});

																					setTimeout(() => {
																						this.props.Interface.loadAchievements(this.props.playerPage.uid, this.props.playerPage.data.project, "history");
																					}, 250);
																				}}
																			/>
																		}
																		empty="No achievements history found"
																	/>
																),
															},
														]}
													/>
												),
											},
										]}
									/>
								</>
							)}
						</>
					}
				/>
				{this.state.callcenterTriggerModalVisible && <PlayerCallcenterTriggerModal visible={true} onDismiss={this.closeCallcenterTriggerModal} playerPage={this.props.playerPage} game={this.props.game} Interface={this.props.Interface} />}
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const playerPage = useRecoilValue(playerPageStore);
		const setPlayerPage = useSetRecoilState(playerPageStore);
		const resetPlayerPage = useResetRecoilState(playerPageStore);
		const game = useRecoilValue(gameStore);
		const playerModal = useRecoilValue(playerModalStore);
		const setPlayerModal = useSetRecoilState(playerModalStore);
		const fortuneWheelModal = useRecoilValue(fortuneWheelModalStore);
		const setFortuneWheelModal = useSetRecoilState(fortuneWheelModalStore);

		const multiWithdrawal = useRecoilValue(multiWithdrawalStore);
		const setMultiWithdrawal = useSetRecoilState(multiWithdrawalStore);
		const resetMultiWithdrawal = useResetRecoilState(multiWithdrawalStore);

		const gameDetailLogModal = useRecoilValue(gameDetailLogModalStore);
		const setGameDetailLogModal = useSetRecoilState(gameDetailLogModalStore);

		const userActionLog = useRecoilValue(userActionLogStore);
		const setUserActionLog = useSetRecoilState(userActionLogStore);
		const resetUserActionLog = useResetRecoilState(userActionLogStore);

		return (
			<Component
				{...props}
				resetUserActionLog={resetUserActionLog}
				resetPlayerPage={resetPlayerPage}
				resetMultiWithdrawal={resetMultiWithdrawal}
				userActionLog={userActionLog}
				setUserActionLog={setUserActionLog}
				multiWithdrawal={multiWithdrawal}
				gameDetailLogModal={gameDetailLogModal}
				setGameDetailLogModal={setGameDetailLogModal}
				setMultiWithdrawal={setMultiWithdrawal}
				fortuneWheelModal={fortuneWheelModal}
				setFortuneWheelModal={setFortuneWheelModal}
				user={user}
				config={config}
				setConfig={setConfig}
				playerPage={playerPage}
				setPlayerPage={setPlayerPage}
				game={game}
				playerModal={playerModal}
				setPlayerModal={setPlayerModal}
			/>
		);
	};
}

export default withRecoil(PlayerProfileModal);
